import React from 'react';
import { useCapabilities } from '../hooks/useCapabilities';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useDatevStatus } from '../features/datev/useDatevStatus';
import { DatevExportModal } from '../features/datev/DatevExportModal';
import { createPortal } from 'react-dom';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { leadDetailPath, leadDocumentPath } from '../utils/leadRoutes';
import { projectDocumentEditorPathFromLabel } from '../utils/projectRoutes';
import { invoicesOverviewPath, invoiceTabFromSlug, type InvoiceTab } from '../utils/invoiceRoutes';
import { InvoiceOutgoingRowMenu } from '../features/invoices/components/InvoiceOutgoingRowMenu';
import { InvoiceDocumentPreviewModal } from '../features/invoices/components/InvoiceDocumentPreviewModal';
import {
  IncomingSepaPreviewModal,
  type IncomingSepaPreview,
  type IncomingSepaPreviewItem,
} from '../features/invoices/components/IncomingSepaPreviewModal';
import { todayYMD } from '../utils/date';
import { formatGermanNumber } from '../utils/parseGermanNumber';
import type {
  ContactOption,
  InvoiceDocument,
  InvoiceStatus,
  LeadOption,
  OrganizationOption,
} from '../features/invoices/types';
import {
  INCOMING_CREDIT_DOCUMENT_TYPE,
  INCOMING_INVOICE_DOCUMENT_TYPE,
  invoiceTabForDocument,
  isIncomingCreditDocumentType,
} from '../features/invoices/utils/incomingInvoice';
import {
  MAX_INVOICE_UPLOAD_FILES,
  prepareInvoiceUploadFiles,
} from '../features/invoices/utils/invoiceUploadFiles';
import {
  contactOptionLabel,
  dateInputValue,
  displayOrDash,
  getDisplayId,
  InvoiceEntityLink,
  IncomingInvoiceDuplicateBanner,
  IncomingInvoiceDuplicateChip,
  IncomingInvoiceReviewChip,
  IncomingReviewAttentionChip,
  IncomingCreditChip,
  invoicePartyHref,
  incomingAuftragSelectOptions,
  invoiceAuftragDisplayLabel,
  invoiceAuftragPatchFromSelect,
  invoiceAuftragSelectValue,
  INVOICE_OVERHEAD_ALLOCATION,
  leadDisplayLabel,
  statusDisplayLabel,
  statusOptionsForTab,
  statusOptionsForRow,
  supplierOrganizations,
  invoiceStatusSelectValue,
  isInvoiceStatusLockedDuringExtraction,
} from '../features/invoices/utils/invoicePreviewUi';
import { isSageImportPaymentField, isSageImportPaymentStatus } from '../features/invoices/utils/sageImportPayment';
import { FILTER_GROUP_LABEL_CLASS } from '../ui/controlStyles';
import {
  AlertCircle,
  ArrowDown,
  ArrowDownLeft,
  ArrowUp,
  ArrowUpRight,
  Bell,
  Calendar,
  Check,
  CheckCircle,
  CheckCircle2,
  CircleDot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Clock,
  Download,
  FileText,
  Landmark,
  Info,
  Lock,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
  Undo2,
  Upload,
  X,
  FileOutput,
} from 'lucide-react';
import { AppSelectDropdown } from '../components/AppSelectDropdown';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CardListSkeleton, TableSkeletonRows } from '../components/TableSkeleton';
import { DocumentUploadProgressOverlay } from '../features/projects/components/DocumentUploadProgress';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { useInvoiceDocumentsLive } from '../hooks/useInvoiceDocumentsLive';
import { useHorizontalWheelScroll } from '../hooks/useHorizontalWheelScroll';
import { CreateLeadModal } from '../features/leads/components/CreateLeadModal';
import { ComposeEmailModal } from '../features/inbox/components/ComposeEmailModal';
import { buildDunningEmail, resolveDunningEmailBodyTemplate } from '../features/invoices/utils/dunningTemplates';
import {
  DUNNING_STAGE_LEAD_DOCUMENT_COLUMN,
  dunningLeadDocumentId,
  dunningPdfFilename,
  dunningStageToDocumentType,
  normalizeDunningDeadlineDays,
} from '../features/invoices/utils/dunningDocument';
import { fetchDispatchAccount } from '../utils/mailDispatchApi';
import { applySendPdfEmailBodyTemplate } from '../features/leads/utils/sendPdfEmailBodyTemplate';
import { resolveDocumentBilling } from '../features/leads/utils/invoiceDebtor';
import { documentTypeLabelDe } from '../features/leads/utils/finalInvoiceDeduction';
import {
  invoiceCustomerColumnLabel,
  invoiceCustomerPartyTarget,
  invoiceSupplierColumnLabel,
  invoiceSupplierPartyTarget,
  isUnassignedIncomingSupplier,
} from '../features/invoices/utils/invoicePartyDisplay';
import { CreateOrganizationModal } from '../features/contacts/CreateOrganizationModal';
import { UI_Z_INDEX } from '../utils/uiZIndex';
import { isDeletableFromOverview } from '../features/invoices/utils/overviewDeletion';
import {
  invoiceReclassifyActionLabel,
  invoiceReclassifyBlockReason,
  invoiceReclassifyTarget,
} from '../features/invoices/utils/invoiceReclassify';
import {
  INVOICE_REJECTION_REASON_LABEL,
  USER_SELECTABLE_REJECTION_REASONS,
  type InvoiceRejectionReason,
} from '../features/invoices/utils/invoiceRejection';
import { shouldOfferCompleteProjectOnPaid } from '../features/invoices/utils/completeProjectOnPaid';
import { isLeadOutcome, outcomeFromStatus } from '../utils/leadPipeline';
import {
  CONTACT_TYPES,
  contactDisplayName,
  contactTypeRequiresCompany,
  contactTypeRequiresLastName,
  leadOutboundEmail,
  type Contact,
  type ContactType,
  type Lead,
  type Organization,
} from '../types';
import { invoiceReceivedAmount } from '../features/invoices/utils/invoiceReceivedAmount';

type EditableInvoiceField =
  | 'lead_ref'
  | 'party_name'
  | 'organization_id'
  | 'issue_date'
  | 'due_date'
  | 'paid_at'
  | 'gross_amount'
  | 'status';

type InvoiceListResponse = {
  items: InvoiceDocument[];
  total: number;
  page: number;
  per_page: number;
};

const INVOICE_PAGE_SIZE = 100;

type InvoiceSortColumn =
  | 'invoice_number'
  | 'lead_display_id'
  | 'party_name'
  | 'issue_date'
  | 'due_date'
  | 'paid_at'
  | 'accounting_forwarded_at'
  | 'gross_amount'
  | 'status'
  | 'dunning_stage';

function InvoiceListSortableHeader({
  label,
  column,
  activeColumn,
  dir,
  onSort,
  className,
  align = 'left',
}: {
  label: string;
  column: InvoiceSortColumn;
  activeColumn: InvoiceSortColumn;
  dir: 'asc' | 'desc';
  onSort: (column: InvoiceSortColumn) => void;
  className?: string;
  align?: 'left' | 'right';
}) {
  const active = activeColumn === column;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`sticky top-0 z-10 border-b border-app-border bg-app-surface-muted ${className ?? 'px-4'} py-2.5 align-middle whitespace-nowrap ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`group/sort inline-flex max-w-full items-center gap-1 text-xs font-medium transition-colors ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        } ${align === 'right' ? 'ml-auto justify-end' : ''}`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

function invoiceListQueryParams(input: {
  tab: InvoiceTab;
  sort: InvoiceSortColumn;
  dir: 'asc' | 'desc';
  page: number;
  perPage: number;
  query?: string;
  selectedStatuses?: Set<string>;
  retentionHeld?: boolean;
  filterYear?: number | null;
  filterMonth?: number | null;
  forKpis?: boolean;
}): URLSearchParams {
  const params = new URLSearchParams({
    type: input.tab,
    sort: input.forKpis ? 'issue_date' : input.sort,
    dir: input.forKpis ? 'desc' : input.dir,
    page: String(input.page),
    per_page: String(input.perPage),
  });

  if (input.forKpis) {
    params.set('status', 'all');
  } else if (input.selectedStatuses && input.selectedStatuses.size > 0) {
    params.set('statuses', [...input.selectedStatuses].sort().join(','));
  }

  if (!input.forKpis && input.query?.trim()) params.set('q', input.query.trim());
  if (!input.forKpis && input.retentionHeld) params.set('retention', 'held');
  if (input.filterYear != null) params.set('issue_year', String(input.filterYear));
  if (input.filterMonth != null) params.set('issue_month', String(input.filterMonth));

  return params;
}

function placePopover(rect: DOMRect, width: number, height: number, gap = 4) {
  const margin = 8;
  const left = Math.min(Math.max(rect.left, margin), Math.max(margin, window.innerWidth - width - margin));
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const opensAbove = spaceBelow < height + gap && spaceAbove > spaceBelow;
  const rawTop = opensAbove ? rect.top - height - gap : rect.bottom + gap;
  const top = Math.min(Math.max(rawTop, margin), Math.max(margin, window.innerHeight - height - margin));
  return { left, top };
}

const TAB_CONFIG: Array<{ id: InvoiceTab; label: string; icon: React.ReactNode }> = [
  { id: 'outgoing', label: 'Ausgangsrechnungen', icon: <ArrowUpRight className="w-4 h-4" /> },
  { id: 'incoming', label: 'Eingangsrechnungen', icon: <ArrowDownLeft className="w-4 h-4" /> },
  { id: 'quotes', label: 'Angebote', icon: <FileText className="w-4 h-4" /> },
];

const MONTH_LABELS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function toNumber(value: number | string | null): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Empfänger-Mail, wenn das Projekt keine liefert: erst die vorab geladene Projektliste,
 * dann ein Kontakt, dessen Name zum Rechnungsempfänger passt.
 */
function fallbackRecipientEmail(
  row: Pick<InvoiceDocument, 'lead_id'>,
  recipientName: string,
  leads: LeadOption[],
  contacts: ContactOption[],
): string {
  if (row.lead_id) {
    // Runtime trägt mehr Felder als LeadOption.
    const fromList = leads.find((l) => String(l.id) === String(row.lead_id));
    const candidate = fromList ? leadOutboundEmail(fromList as unknown as Partial<Lead>) : '';
    if (candidate) return candidate;
  }
  if (!recipientName) return '';
  const needle = recipientName.toLowerCase();
  const fromContacts = contacts.find((c) => {
    const candidates = [c.name, contactDisplayName(c), c.company, `${c.first_name ?? ''} ${c.last_name ?? ''}`]
      .map((v) => (v ?? '').toString().trim().toLowerCase())
      .filter(Boolean);
    return candidates.some((v) => v === needle || v.includes(needle) || needle.includes(v));
  });
  return fromContacts?.email?.trim() ?? '';
}

function formatMoney(value: number | string | null, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(toNumber(value));
}

/** Rechnungsbetrag wie auf dem Beleg; gezogenes Skonto als Zusatzzeile darunter. */
function InvoiceAmountDisplay({ row }: { row: InvoiceDocument }) {
  const currency = row.currency || 'EUR';
  const skonto = toNumber(row.skonto_amount);
  return (
    <>
      {formatMoney(row.gross_amount, currency)}
      {row.skonto_taken && skonto > 0 ? (
        <span className="block text-[11px] font-normal text-emerald-700 dark:text-emerald-400">
          −{formatMoney(skonto, currency)} Skonto
        </span>
      ) : null}
    </>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('de-DE');
}

function localDateIso(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dueDateTextClass(row: InvoiceDocument): string {
  const due = dateInputValue(row.due_date);
  if (!due || row.status === 'paid' || row.status === 'cancelled') return '';
  if (row.status === 'overdue') return 'text-rose-700 font-semibold';
  if (due === localDateIso() || due === localDateIso(1)) return 'text-amber-700 font-semibold';
  return '';
}

function statusBadgeClass(status: Exclude<InvoiceStatus, 'all'>): string {
  if (status === 'paid') return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  if (status === 'cancelled') return 'bg-app-surface-muted text-app-text-muted border-app-border';
  if (status === 'overdue' || status === 'extraction_failed' || status === 'rejected') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

function statusDotClass(status: Exclude<InvoiceStatus, 'all'>): string {
  if (status === 'paid') return 'bg-cyan-500';
  if (status === 'cancelled') return 'bg-slate-400';
  if (status === 'overdue' || status === 'extraction_failed' || status === 'rejected') return 'bg-rose-500';
  return 'bg-blue-500';
}

function statusTextClass(status: Exclude<InvoiceStatus, 'all'>): string {
  if (status === 'paid') return 'text-cyan-700';
  if (status === 'cancelled') return 'text-app-text-muted';
  if (status === 'overdue' || status === 'extraction_failed' || status === 'rejected') return 'text-rose-700';
  return 'text-blue-700';
}

function StatusIcon({ status }: { status: Exclude<InvoiceStatus, 'all'> }) {
  if (status === 'paid') return <CheckCircle className="w-3 h-3" />;
  if (status === 'cancelled') return <Undo2 className="w-3 h-3" />;
  if (status === 'overdue' || status === 'extraction_failed' || status === 'rejected') return <AlertCircle className="w-3 h-3" />;
  return <Clock className="w-3 h-3" />;
}

function statusFilterPillClass(status: Exclude<InvoiceStatus, 'all'>, selected: boolean): string {
  if (!selected) {
    return 'border-app-border bg-app-surface text-slate-400 hover:bg-app-hover';
  }
  if (status === 'paid') {
    return 'border-app-border-strong bg-app-surface-muted text-app-text-muted';
  }
  if (status === 'overdue' || status === 'extraction_failed' || status === 'rejected') {
    return 'border-rose-300 bg-rose-50 text-rose-700';
  }
  if (status === 'cancelled') {
    return 'border-blue-300 bg-blue-50 text-blue-700';
  }
  return 'border-blue-300 bg-blue-50 text-blue-700';
}

/** Outgoing tab: focus on open pipeline — paid, errors, cancelled off by default (pills can be enabled). */
const INVOICE_STATUS_FILTER_KEYS = new Set(['open', 'overdue', 'paid', 'draft', 'extraction_failed', 'rejected', 'cancelled']);

function loadingLabelForTab(tab: InvoiceTab): string {
  if (tab === 'quotes') return 'Angebote werden geladen …';
  if (tab === 'incoming') return 'Eingangsrechnungen werden geladen …';
  return 'Ausgangsrechnungen werden geladen …';
}

function defaultSelectedStatusesForTab(tab: InvoiceTab): Set<string> {
  if (tab === 'outgoing') {
    return new Set(['open', 'overdue']);
  }
  if (tab === 'incoming') {
    return new Set(['draft', 'open', 'overdue', 'extraction_failed']);
  }
  return new Set(['open', 'overdue', 'extraction_failed', 'paid', 'cancelled']);
}

type DunningStage = 'reminder' | '1' | '2' | '3';

const DUNNING_STAGE_TITLE: Record<DunningStage, string> = {
  reminder: 'Erinnerung',
  '1': '1. Mahnung',
  '2': '2. Mahnung',
  '3': '3. Mahnung',
};

function dunningSentAt(row: InvoiceDocument, stage: DunningStage): string | null {
  if (stage === 'reminder') return row.reminder_sent_at;
  if (stage === '1') return row.dunning_1_sent_at;
  if (stage === '2') return row.dunning_2_sent_at;
  return row.dunning_3_sent_at;
}

function getActiveDunningStage(row: InvoiceDocument): DunningStage {
  if (row.dunning_3_sent_at) return '3';
  if (row.dunning_2_sent_at) return '3';
  if (row.dunning_1_sent_at) return '2';
  if (row.reminder_sent_at) return '1';
  return 'reminder';
}

function dueDeltaDays(row: InvoiceDocument): number | null {
  const due = dateInputValue(row.due_date);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(`${due}T00:00:00`);
  if (Number.isNaN(dueDate.getTime())) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((dueDate.getTime() - today.getTime()) / msPerDay);
}

function isDunningStageEnabled(stage: DunningStage, row: InvoiceDocument): boolean {
  if (row.status === 'paid' || row.status === 'cancelled') return false;
  if (stage === 'reminder') return true;
  if (stage === '1') {
    const delta = dueDeltaDays(row);
    return delta != null && delta < 0;
  }
  if (stage === '2') return Boolean(row.dunning_1_sent_at);
  return Boolean(row.dunning_2_sent_at);
}

function dueRelativeLabel(row: InvoiceDocument): string | null {
  if (row.status === 'paid' || row.status === 'cancelled') return null;
  const delta = dueDeltaDays(row);
  if (delta == null) return null;
  if (delta < 0) {
    const days = Math.abs(delta);
    return `${days} ${days === 1 ? 'Tag' : 'Tage'} überfällig`;
  }
  if (delta === 0) return 'Heute fällig';
  if (delta === 1) return 'Morgen fällig';
  return `Fällig in ${delta} Tagen`;
}

function dueRelativeColorClass(row: InvoiceDocument): string {
  if (row.status === 'paid' || row.status === 'cancelled') return 'text-slate-400';
  const delta = dueDeltaDays(row);
  if (delta == null) return 'text-slate-400';
  if (delta < 0) return 'text-rose-700';
  if (delta <= 1) return 'text-amber-700';
  return 'text-app-text-subtle';
}

function AccountingForwardCell({
  row,
  busy,
  onForward,
}: {
  row: InvoiceDocument;
  busy: boolean;
  onForward: () => void;
}) {
  if (row.accounting_forwarded_at) {
    return (
      <span
        className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 px-1 text-emerald-700"
        title={`An Buchhaltung weitergeleitet${row.accounting_forwarded_at ? ` am ${formatDate(row.accounting_forwarded_at)}` : ''}`}
      >
        <Check className="w-3 h-3" aria-hidden />
        <span className="sr-only">An Buchhaltung weitergeleitet</span>
      </span>
    );
  }
  const err = String(row.accounting_forward_error ?? '').trim();
  // Aus Sage übernommen: kein PDF, kein Versand. Der Klick vermerkt nur die Übergabe.
  const sageImport = row.source === 'sage_import';
  const label = sageImport
    ? 'Als an Buchhaltung übergeben markieren (aus Sage, kein Versand)'
    : err
      ? `Weiterleitung fehlgeschlagen: ${err}. Erneut senden.`
      : 'An Buchhaltung senden';
  return (
    <button
      type="button"
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation();
        onForward();
      }}
      title={label}
      className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-md border px-1 transition-colors disabled:opacity-50 ${
        err
          ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
          : 'border-app-border bg-app-surface text-app-text-subtle hover:bg-app-hover hover:text-blue-700'
      }`}
    >
      {busy ? <LoadingSpinner size="xs" /> : <RefreshCw className="w-3 h-3" aria-hidden />}
      <span className="sr-only">{label}</span>
    </button>
  );
}

function InvoiceMobileCard({
  row,
  tab,
  customerLabel,
  customerHref,
  supplierLabel,
  supplierHref,
  onPreview,
  onEditStatus,
  reviewStaleDays,
}: {
  row: InvoiceDocument;
  tab: InvoiceTab;
  reviewStaleDays: number;
  customerLabel: string;
  customerHref: string | null;
  supplierLabel: string;
  supplierHref: string | null;
  onPreview: () => void;
  onEditStatus: () => void;
}) {
  const statusLocked = isInvoiceStatusLockedDuringExtraction(row.status);
  const dueRel = dueRelativeLabel(row);
  const leadLabel = row.lead_display_id || (row.lead_id ? row.lead_id.slice(0, 8) : '');
  const customerText = displayOrDash(customerLabel);
  const supplierText = displayOrDash(supplierLabel);
  return (
    <div className="border-b border-app-border bg-app-surface px-3 py-3.5">
      <button type="button" onClick={onPreview} className="w-full min-w-0 text-left active:opacity-80">
        <div className="flex items-start justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className="text-sm font-semibold text-app-text truncate">{getDisplayId(row)}</span>
            {tab === 'incoming' && row.duplicate_of ? <IncomingInvoiceDuplicateChip /> : null}
            {tab === 'incoming' ? <IncomingInvoiceReviewChip row={row} /> : null}
            {tab === 'incoming' ? <IncomingReviewAttentionChip row={row} staleDays={reviewStaleDays} /> : null}
          </span>
          <span className="text-right text-sm font-semibold text-app-text tabular-nums shrink-0">
            <InvoiceAmountDisplay row={row} />
          </span>
        </div>
      </button>
      <p className="mt-0.5 text-xs text-app-text-subtle truncate">
        {row.lead_id && leadLabel ? (
          <InvoiceEntityLink to={leadDetailPath({ id: row.lead_id })} title="Auftrag öffnen">
            {leadLabel}
          </InvoiceEntityLink>
        ) : invoiceAuftragDisplayLabel(row) || (
          '—'
        )}
      </p>
      <p className="mt-0.5 text-xs text-app-text-subtle truncate">
        <span className="text-slate-400">Kunde </span>
        {customerLabel && customerHref ? (
          <InvoiceEntityLink to={customerHref} title="Kunde öffnen">
            {customerText}
          </InvoiceEntityLink>
        ) : (
          customerText
        )}
        {tab === 'incoming' ? (
          <>
            <span className="text-slate-300"> · </span>
            <span className="text-slate-400">Lieferant </span>
            {isUnassignedIncomingSupplier(row) ? (
              <UnassignedSupplierLabel name={supplierText} />
            ) : supplierLabel && supplierHref ? (
              <InvoiceEntityLink to={supplierHref} title="Lieferant öffnen">
                {supplierText}
              </InvoiceEntityLink>
            ) : (
              supplierText
            )}
          </>
        ) : null}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={onEditStatus}
          disabled={statusLocked}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium active:scale-[0.98] disabled:opacity-70 disabled:active:scale-100 ${statusBadgeClass(row.status)}`}
          aria-label={statusLocked ? 'Status wird extrahiert' : 'Status ändern'}
        >
          <StatusIcon status={row.status} />
          {statusDisplayLabel(row.status, tab, row)}
          <ChevronDown className="w-3 h-3 opacity-70" aria-hidden />
        </button>
        {dueRel ? (
          <span className={`text-xs font-medium ${dueRelativeColorClass(row)}`}>{dueRel}</span>
        ) : row.due_date ? (
          <span className={`text-xs ${dueDateTextClass(row) || 'text-slate-400'}`}>
            Fällig {formatDate(row.due_date)}
          </span>
        ) : null}
        {row.issue_date ? (
          <span className="text-xs text-slate-400 tabular-nums">{formatDate(row.issue_date)}</span>
        ) : null}
        {tab === 'outgoing' && toNumber(row.retention_gross) > 0 ? (
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
              row.retention_status === 'held'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-app-border bg-app-surface-muted text-app-text-subtle'
            }`}
          >
            {row.retention_status === 'held' ? <Lock className="h-3 w-3" /> : null}
            Einbehalt {formatMoney(row.retention_gross, row.currency || 'EUR')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MobileStatusPickerSheet({
  row,
  tab,
  saving,
  onSave,
  onClose,
}: {
  row: InvoiceDocument;
  tab: InvoiceTab;
  saving: boolean;
  onSave: (status: Exclude<InvoiceStatus, 'all'>) => void;
  onClose: () => void;
}) {
  const current = invoiceStatusSelectValue(row.status, tab);
  const options = statusOptionsForRow(tab, row);
  const extractionLocked = isInvoiceStatusLockedDuringExtraction(row.status);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[280] md:hidden" role="dialog" aria-modal="true" aria-label="Status ändern">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-label="Schließen"
      />
      <div className="absolute bottom-0 left-0 right-0 rounded-t-xl bg-app-surface shadow-2xl border-t border-app-border pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex justify-center pt-2 pb-1" aria-hidden>
          <span className="h-1 w-10 rounded-full bg-slate-200" />
        </div>
        <div className="px-4 pb-2">
          <p className="text-sm font-semibold text-app-text">Status ändern</p>
          <p className="text-xs text-app-text-subtle truncate">{getDisplayId(row)}</p>
        </div>
        <div className="px-2 pb-2 space-y-0.5">
          {options.map((option) => {
            const selected = current === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={saving || extractionLocked}
                onClick={() => onSave(option.id)}
                className={`flex w-full items-center gap-3 rounded-md px-3 h-11 text-left disabled:opacity-50 ${
                  selected ? 'bg-app-surface-muted' : 'active:bg-app-surface-muted'
                }`}
              >
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(option.id)}`}
                >
                  <StatusIcon status={option.id} />
                  {statusDisplayLabel(option.id, tab, row)}
                </span>
                {selected ? <Check className="ml-auto w-4 h-4 text-blue-600 shrink-0" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DunningStageCell({
  row,
  onSendStage,
  busyStage,
}: {
  row: InvoiceDocument;
  onSendStage: (stage: DunningStage) => void;
  busyStage: DunningStage | null;
}) {
  const activeStage = getActiveDunningStage(row);
  const delta = dueDeltaDays(row);
  const isPaid = row.status === 'paid' || row.status === 'cancelled';
  const dueLabel = dueRelativeLabel(row);
  const dueColor = dueRelativeColorClass(row);

  const stageOrder: DunningStage[] = ['reminder', '1', '2', '3'];
  const numberLabel: Record<DunningStage, string> = { reminder: '', '1': '1', '2': '2', '3': '3' };

  const renderActiveBadge = (stage: DunningStage) => {
    const enabled = isDunningStageEnabled(stage, row);
    const rowBusy = busyStage != null;
    const isBusy = busyStage === stage;
    const reminderColored = stage === 'reminder' && enabled && delta != null && delta <= 1;
    const dunningColored = stage !== 'reminder' && enabled && delta != null && delta < 0;
    const baseColor =
      stage === 'reminder'
        ? reminderColored
          ? 'bg-amber-50 border-amber-300 text-amber-700 ring-1 ring-amber-200'
          : 'bg-app-surface border-app-border text-app-text-muted'
        : dunningColored
          ? 'bg-rose-50 border-rose-300 text-rose-700 ring-1 ring-rose-200'
          : 'bg-app-surface border-app-border text-app-text-muted';
    const disabledClass = isBusy
      ? 'cursor-wait'
      : enabled
        ? 'hover:bg-app-hover cursor-pointer'
        : 'opacity-60 cursor-not-allowed';
    const label = stage === 'reminder' ? 'Erinnern' : stage === '1' ? '1. Mahnung' : stage === '2' ? '2. Mahnung' : '3. Mahnung';
    return (
      <button
        key={stage}
        type="button"
        disabled={!enabled || rowBusy}
        onClick={() => enabled && !rowBusy && onSendStage(stage)}
        title={enabled ? `${DUNNING_STAGE_TITLE[stage]} senden` : 'Noch nicht verfügbar'}
        aria-busy={isBusy}
        className={`relative inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium ${baseColor} ${disabledClass}`}
      >
        <span className={`inline-flex items-center gap-1 ${isBusy ? 'invisible' : ''}`}>
          {stage === 'reminder' ? <Bell className="w-3 h-3" /> : null}
          <span>{label}</span>
        </span>
        {isBusy ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner size="xs" />
          </span>
        ) : null}
      </button>
    );
  };

  const renderMiniPill = (stage: DunningStage, highlighted = false) => {
    const sentAt = dunningSentAt(row, stage);
    if (sentAt) {
      return (
        <span
          key={stage}
          title={`${DUNNING_STAGE_TITLE[stage]} verschickt am ${formatDate(sentAt)}`}
          className="inline-flex items-center justify-center h-6 min-w-[24px] px-1 rounded-md text-emerald-700"
        >
          <Check className="w-3 h-3" />
        </span>
      );
    }
    const enabled = isDunningStageEnabled(stage, row);
    const rowBusy = busyStage != null;
    const isBusy = busyStage === stage;
    const highlightedOn = highlighted && enabled;
    const cls = highlightedOn
      ? `bg-rose-500/15 text-rose-700 ${isBusy ? 'cursor-wait' : 'hover:bg-rose-500/25 cursor-pointer'}`
      : enabled
        ? `text-app-text-subtle ${isBusy ? 'cursor-wait' : 'hover:bg-app-hover hover:text-app-text cursor-pointer'}`
        : 'text-app-text-subtle/45 cursor-not-allowed';
    return (
      <button
        key={stage}
        type="button"
        disabled={!enabled || rowBusy}
        onClick={() => enabled && !rowBusy && onSendStage(stage)}
        title={enabled ? `${DUNNING_STAGE_TITLE[stage]} senden` : `${DUNNING_STAGE_TITLE[stage]} noch nicht verfügbar`}
        aria-busy={isBusy}
        className={`relative inline-flex items-center justify-center h-6 min-w-[24px] px-1 rounded-md text-xs font-medium transition-colors ${cls}`}
      >
        <span className={isBusy ? 'invisible' : undefined}>
          {stage === 'reminder' ? <Bell className="w-3 h-3" /> : numberLabel[stage]}
        </span>
        {isBusy ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner size="xs" />
          </span>
        ) : null}
      </button>
    );
  };

  if (isPaid) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const overdueWithoutReminder = activeStage === 'reminder' && delta != null && delta < 0;
  const sentList: Array<{ stage: DunningStage; sentAt: string }> = stageOrder
    .map((stage) => ({ stage, sentAt: dunningSentAt(row, stage) }))
    .filter((entry): entry is { stage: DunningStage; sentAt: string } => Boolean(entry.sentAt));

  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="inline-flex items-center gap-1">
        {stageOrder.map((stage) =>
          stage === activeStage
            ? renderActiveBadge(stage)
            : renderMiniPill(stage, overdueWithoutReminder && stage === '1'),
        )}
      </div>
      <div className="relative group">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Mahnstufen-Details"
          className="inline-flex items-center justify-center h-5 w-5 rounded-full text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
        <div className="pointer-events-none absolute right-0 top-full z-30 mt-1 hidden w-56 rounded-md border border-app-border bg-app-surface p-2 text-xs text-app-text-muted shadow-lg group-hover:block">
          {dueLabel ? (
            <div className={`font-medium ${dueColor}`}>{dueLabel}</div>
          ) : (
            <div className="text-app-text-subtle">Kein Fälligkeitsdatum hinterlegt.</div>
          )}
          {sentList.length > 0 ? (
            <ul className="mt-1.5 space-y-0.5 border-t border-app-border pt-1.5">
              {sentList.map(({ stage, sentAt }) => (
                <li key={stage} className="flex items-center justify-between gap-2 text-app-text-muted">
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-600" />
                    {DUNNING_STAGE_TITLE[stage]}
                  </span>
                  <span className="text-app-text-subtle">{formatDate(sentAt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-1.5 border-t border-app-border pt-1.5 text-slate-400">
              Noch keine Mahnung verschickt.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getEditableValue(row: InvoiceDocument, field: EditableInvoiceField, tab: InvoiceTab): string {
  if (field === 'lead_ref') return row.lead_display_id || row.lead_id || '';
  if (field === 'party_name') return row.party_name || '';
  if (field === 'organization_id') return row.organization_id || '';
  if (field === 'issue_date') return dateInputValue(row.issue_date);
  if (field === 'due_date') return dateInputValue(row.due_date);
  if (field === 'paid_at') return dateInputValue(row.paid_at);
  if (field === 'gross_amount') {
    const amount = Number(row.gross_amount);
    if (row.gross_amount == null || !Number.isFinite(amount)) return '';
    return formatGermanNumber(amount);
  }
  return invoiceStatusSelectValue(row.status, tab);
}

function CreateContactModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (contact: ContactOption) => void;
}) {
  const [contactType, setContactType] = React.useState<ContactType>('Lieferant');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(contactTypeRequiresCompany(contactType) ? 'Bitte Unternehmensnamen angeben.' : 'Bitte Namen angeben.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { contact_type: contactType, email: email.trim() || null };
      if (contactTypeRequiresCompany(contactType)) body.company = trimmedName;
      else if (contactTypeRequiresLastName(contactType)) body.last_name = trimmedName;
      else body.name = trimmedName;
      const response = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Kontakt konnte nicht erstellt werden.');
      onCreated(payload as ContactOption);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kontakt konnte nicht erstellt werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[280] flex items-center justify-center bg-slate-900/45 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl bg-app-surface shadow-2xl border border-app-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border bg-app-surface-muted">
          <h2 className="text-base font-semibold text-app-text">Neuen Kontakt erstellen</h2>
          <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-medium text-app-text-muted">
            Kontaktart
            <select value={contactType} onChange={(e) => setContactType(e.target.value as ContactType)} className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-sm text-app-text">
              {CONTACT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-app-text-muted">
            {contactTypeRequiresCompany(contactType) ? 'Unternehmen' : 'Name'}
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-sm text-app-text" />
          </label>
          <label className="block text-xs font-medium text-app-text-muted">
            E-Mail optional
            <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-sm text-app-text" />
          </label>
          {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-app-border bg-app-surface-muted">
          <button type="button" onClick={onClose} className="rounded-lg border border-app-border bg-app-surface px-4 py-2 text-sm font-medium text-app-text-muted hover:bg-app-hover">Abbrechen</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Speichert…' : 'Kontakt erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Lieferantenname nur aus dem Beleg, keine Organisation zugeordnet. */
function UnassignedSupplierLabel({ name }: { name: string }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 text-app-text-subtle"
      title="Lieferant aus dem Beleg erkannt, aber nicht als Organisation angelegt. Klicken zum Zuordnen oder Anlegen."
    >
      <span className="truncate italic">{name}</span>
      <span className="shrink-0 rounded bg-amber-500/10 px-1 py-px text-[10px] font-medium not-italic text-amber-700 dark:text-amber-400">
        nicht angelegt
      </span>
    </span>
  );
}

function InlineSelectPopover<T extends { id: string | number }>({
  search,
  onSearch,
  items,
  renderItem,
  onSelect,
  pinnedItems,
  onSelectPinned,
  onCreate,
  onClose,
  createLabel,
  placeholder,
}: {
  search: string;
  onSearch: (value: string) => void;
  items: T[];
  renderItem: (item: T) => string;
  onSelect: (item: T) => void;
  pinnedItems?: Array<{ id: string; label: string }>;
  onSelectPinned?: (id: string) => void;
  onCreate?: () => void;
  onClose: () => void;
  createLabel?: string;
  placeholder: string;
}) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ left: number; top: number; width: number } | null>(null);

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(Math.max(320, rect.width), 352);
      const { left, top } = placePopover(rect, width, 360);
      setPosition({ left, top, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, []);

  React.useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const query = search.trim().toLowerCase();
  const visiblePinned = (pinnedItems ?? []).filter((item) => !query || item.label.toLowerCase().includes(query));

  const content = (
    <div
      ref={popoverRef}
      className="z-[300] max-h-[360px] overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-xl"
      style={{
        position: 'fixed',
        left: position?.left ?? 16,
        top: position?.top ?? 16,
        width: position?.width ?? 320,
      }}
    >
      <div className="p-2 border-b border-app-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            autoFocus
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-md border border-app-border py-1.5 pl-8 pr-3 text-xs outline-none focus:border-blue-400"
          />
        </div>
      </div>
      {visiblePinned.length > 0 ? (
        <div className="border-b border-app-border">
          {visiblePinned.map((item) => (
            <button
              key={item.id || '__unassigned__'}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelectPinned?.(item.id)}
              className="block w-full truncate px-3 py-2 text-left text-xs font-medium text-app-text hover:bg-app-hover"
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
      {onCreate && createLabel ? (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onCreate} className="w-full border-b border-app-border px-3 py-2 text-left text-xs font-medium text-blue-700 hover:bg-blue-50">
          {createLabel}
        </button>
      ) : null}
      <div className="max-h-64 overflow-y-auto overflow-x-hidden">
        {items.length === 0 ? (
          visiblePinned.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-400">Keine Treffer.</div>
          ) : null
        ) : items.map((item) => (
          <button key={String(item.id)} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onSelect(item)} className="block w-full truncate px-3 py-2 text-left text-xs text-app-text hover:bg-app-hover">
            {renderItem(item)}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <span ref={anchorRef} className="inline-block h-px w-full" />
      {createPortal(content, document.body)}
    </>
  );
}

function DateEditPopover({
  value,
  onSave,
  onClose,
}: {
  value: string;
  onSave: (value: string) => void;
  onClose: () => void;
}) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null);
  const initialDate = React.useMemo(() => {
    const parsed = value ? new Date(`${value}T12:00:00`) : new Date();
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [value]);
  const [visibleMonth, setVisibleMonth] = React.useState(() => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition(placePopover(rect, 256, 344));
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, []);

  React.useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const selectedIso = value;
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayOffset = index - mondayOffset + 1;
    const date = new Date(year, month, dayOffset);
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    let day = dayOffset;
    if (dayOffset <= 0) day = previousMonthDays + dayOffset;
    if (dayOffset > daysInMonth) day = dayOffset - daysInMonth;
    return { date, iso, day, isCurrentMonth: date.getMonth() === month };
  });

  const monthLabel = visibleMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const todayIso = new Date().toISOString().slice(0, 10);

  const content = (
    <div
      ref={popoverRef}
      className="z-[300] w-64 rounded-xl border border-app-border bg-app-surface p-3 shadow-xl"
      style={{
        position: 'fixed',
        left: position?.left ?? 16,
        top: position?.top ?? 16,
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
          className="h-7 w-7 rounded-lg text-xs text-app-text-subtle hover:bg-app-hover hover:text-app-text"
          aria-label="Vorheriger Monat"
        >
          ‹
        </button>
        <div className="text-xs font-semibold text-app-text capitalize">{monthLabel}</div>
        <button
          type="button"
          onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
          className="h-7 w-7 rounded-lg text-xs text-app-text-subtle hover:bg-app-hover hover:text-app-text"
          aria-label="Nächster Monat"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-app-text-subtle">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => <div key={day}>{day}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((cell) => {
          const isSelected = cell.iso === selectedIso;
          const isToday = cell.iso === todayIso;
          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onSave(cell.iso)}
              className={`h-7 rounded-lg text-xs transition-colors ${
                isSelected
                  ? 'bg-blue-600 font-semibold text-white'
                  : isToday
                    ? 'bg-blue-50 font-semibold text-blue-700 hover:bg-blue-100'
                    : cell.isCurrentMonth
                      ? 'text-app-text hover:bg-app-hover'
                      : 'text-slate-300 hover:bg-app-hover'
              }`}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-app-border pt-2">
        <button type="button" onClick={() => onSave('')} className="text-xs font-medium text-app-text-subtle hover:text-rose-700">
          Löschen
        </button>
        <button type="button" onClick={() => onSave(todayIso)} className="text-xs font-medium text-blue-700 hover:text-blue-800">
          Heute
        </button>
      </div>
    </div>
  );

  return (
    <>
      <span ref={anchorRef} className="inline-block h-px w-full" />
      {createPortal(content, document.body)}
    </>
  );
}

function StyledSelect({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: number | null;
  options: Array<{ value: number; label: string }>;
  placeholder: string;
  onChange: (next: number | null) => void;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <div ref={containerRef} className="relative mt-1">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`w-full inline-flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-xs outline-none transition-colors ${
          open
            ? 'border-blue-400 bg-app-surface text-app-text'
            : 'border-app-border bg-app-surface text-app-text hover:border-app-border-strong'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? 'truncate' : 'truncate text-slate-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 z-[310] mt-1 max-h-52 overflow-y-auto rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={value == null}
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className={`block w-full px-3 py-1.5 text-left text-xs ${
              value == null ? 'bg-blue-50 font-medium text-blue-700' : 'text-app-text-subtle hover:bg-app-hover'
            }`}
          >
            {placeholder}
          </button>
          <div className="my-1 border-t border-app-border" />
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs ${
                  isSelected ? 'bg-blue-50 font-medium text-blue-700' : 'text-app-text hover:bg-app-hover'
                }`}
              >
                <span className="truncate">{option.label}</span>
                {isSelected ? <Check className="w-3.5 h-3.5 text-blue-600" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PeriodFilterPopover({
  anchorRef,
  year,
  month,
  availableYears,
  onChange,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  year: number | null;
  month: number | null;
  availableYears: number[];
  onChange: (year: number | null, month: number | null) => void;
  onClose: () => void;
}) {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null);

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition(placePopover(rect, 256, 232));
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef]);

  React.useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorRef, onClose]);

  const content = (
    <div
      ref={popoverRef}
      className="z-[300] w-64 rounded-xl border border-app-border bg-app-surface p-3 shadow-xl"
      style={{
        position: 'fixed',
        left: position?.left ?? 16,
        top: position?.top ?? 16,
      }}
    >
      <div className={`mb-2 ${FILTER_GROUP_LABEL_CLASS}`}>
        Zeitraum filtern
      </div>
      <div className="space-y-2">
        <div className={`block ${FILTER_GROUP_LABEL_CLASS}`}>
          Jahr
          <StyledSelect
            value={year}
            placeholder="Alle Jahre"
            options={availableYears.map((y) => ({ value: y, label: String(y) }))}
            onChange={(nextYear) => onChange(nextYear, month)}
          />
        </div>
        <div className={`block ${FILTER_GROUP_LABEL_CLASS}`}>
          Monat
          <StyledSelect
            value={month}
            placeholder="Alle Monate"
            options={MONTH_LABELS.map((label, index) => ({ value: index + 1, label }))}
            onChange={(nextMonth) => onChange(year, nextMonth)}
          />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-app-border pt-2">
        <button
          type="button"
          onClick={() => onChange(null, null)}
          disabled={year == null && month == null}
          className="text-xs font-medium text-app-text-subtle hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-app-text-subtle"
        >
          Zurücksetzen
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-blue-700 hover:text-blue-800"
        >
          Schließen
        </button>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function StatusEditPopover({
  value,
  tab,
  row,
  onSave,
  onClose,
}: {
  value: Exclude<InvoiceStatus, 'all'>;
  tab: InvoiceTab;
  row: Pick<InvoiceDocument, 'document_type' | 'source' | 'status' | 'approved_at'>;
  onSave: (value: Exclude<InvoiceStatus, 'all'>) => void;
  onClose: () => void;
}) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ left: number; top: number; width: number } | null>(null);

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(Math.max(140, rect.width), 180);
      const { left, top } = placePopover(rect, width, 176);
      setPosition({ left, top, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, []);

  React.useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const options = statusOptionsForRow(tab, row).filter(
    (option) => row.source !== 'sage_import' || isSageImportPaymentStatus(option.id, row.document_type),
  );
  const content = (
    <div
      ref={popoverRef}
      className="z-[300] overflow-hidden rounded-lg border border-app-border bg-app-surface py-1 shadow-xl"
      style={{
        position: 'fixed',
        left: position?.left ?? 16,
        top: position?.top ?? 16,
        width: position?.width ?? 140,
      }}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onSave(option.id)}
          className="block w-full px-3 py-2 text-left hover:bg-app-hover"
        >
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(option.id)} ${value === option.id ? 'ring-1 ring-blue-200' : ''}`}>
            <StatusIcon status={option.id} />
            {statusDisplayLabel(option.id, tab, row)}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <span ref={anchorRef} className="inline-block h-px w-full" />
      {createPortal(content, document.body)}
    </>
  );
}

function DeleteOfferModal({
  row,
  onClose,
  onDeleted,
}: {
  row: InvoiceDocument;
  onClose: () => void;
  onDeleted: (row: InvoiceDocument) => void;
}) {
  const [deleting, setDeleting] = React.useState(false);
  const [deleted, setDeleted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Angebot konnte nicht gelöscht werden.');
      onDeleted(row);
      setDeleted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Angebot konnte nicht gelöscht werden.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[280] flex items-center justify-center bg-slate-900/45 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget && !deleting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-app-border bg-app-surface shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border bg-app-surface-muted">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg inline-flex items-center justify-center ${deleted ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {deleted ? <CheckCircle className="w-5 h-5" /> : <Trash2 className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-app-text">{deleted ? 'Angebot gelöscht' : 'Angebot löschen?'}</h2>
              <p className="text-xs text-app-text-subtle mt-0.5">{getDisplayId(row)}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={deleting} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-app-text-muted disabled:opacity-50">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {deleted ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Das Angebot wurde aus der Übersicht entfernt und liegt 28 Tage im Papierkorb.
            </div>
          ) : (
            <p className="text-xs leading-5 text-app-text-muted">
              Möchtest du dieses Angebot wirklich aus der Übersicht löschen? Es landet für 28 Tage im Papierkorb und
              kann von dort wiederhergestellt werden. Das ursprüngliche Dokument im Auftrag bleibt erhalten.
            </p>
          )}
          {error && (
            <div className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-app-border bg-app-surface-muted">
          <button type="button" onClick={onClose} disabled={deleting} className="rounded-lg border border-app-border bg-app-surface px-4 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50">
            {deleted ? 'Schließen' : 'Abbrechen'}
          </button>
          {!deleted && (
            <button type="button" onClick={confirmDelete} disabled={deleting} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50">
              {deleting && <LoadingSpinner />}
              Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function defaultUploadDocumentType(tab: InvoiceTab, item?: InvoiceDocument | null): InvoiceDocument['document_type'] {
  if (tab === 'quotes') return 'offer';
  if (tab === 'incoming') {
    return item && isIncomingCreditDocumentType(item.document_type)
      ? INCOMING_CREDIT_DOCUMENT_TYPE
      : INCOMING_INVOICE_DOCUMENT_TYPE;
  }
  return item?.document_type && item.document_type !== 'offer' ? item.document_type : 'invoice';
}

function UploadInvoiceModal({
  onClose,
  onSaved,
  tab,
  leads,
  contacts,
  organizations,
  onCreateLead,
  onCreateContact,
}: {
  onClose: () => void;
  onSaved: () => void;
  tab: InvoiceTab;
  leads: LeadOption[];
  contacts: ContactOption[];
  organizations: OrganizationOption[];
  onCreateLead?: () => void;
  onCreateContact?: () => void;
}) {
  const [pickedFiles, setPickedFiles] = React.useState<File[]>([]);
  const [uploadedItems, setUploadedItems] = React.useState<InvoiceDocument[]>([]);
  const [reviewIndex, setReviewIndex] = React.useState(0);
  const [uploadProgress, setUploadProgress] = React.useState<{ completed: number; total: number } | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [batchErrors, setBatchErrors] = React.useState<string[]>([]);
  const [extractionErrors, setExtractionErrors] = React.useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = React.useState(false);
  const [documentType, setDocumentType] = React.useState<InvoiceDocument['document_type']>(
    defaultUploadDocumentType(tab),
  );
  const [draft, setDraft] = React.useState({
    party_name: '',
    invoice_number: '',
    issue_date: '',
    due_date: '',
    gross_amount: '',
    currency: 'EUR',
    lead_id: '',
    organization_id: '',
  });
  const [leadSearch, setLeadSearch] = React.useState('');
  const [contactSearch, setContactSearch] = React.useState('');
  const [leadOpen, setLeadOpen] = React.useState(false);
  const [contactOpen, setContactOpen] = React.useState(false);

  const uploadedItemsRef = React.useRef<InvoiceDocument[]>([]);
  const savedIdsRef = React.useRef<Set<string>>(new Set());
  const cancelledRef = React.useRef(false);
  const uploadInFlightRef = React.useRef(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const item = uploadedItems[reviewIndex] ?? null;

  React.useEffect(() => {
    uploadedItemsRef.current = uploadedItems;
  }, [uploadedItems]);

  React.useEffect(() => {
    if (!item) return;
    setDraft({
      party_name: item.party_name ?? '',
      invoice_number: item.invoice_number ?? '',
      issue_date: item.issue_date ?? '',
      due_date: item.due_date ?? '',
      gross_amount: item.gross_amount != null ? String(item.gross_amount) : '',
      currency: 'EUR',
      lead_id: invoiceAuftragSelectValue(item),
      organization_id: item.organization_id ?? '',
    });
    setDocumentType(defaultUploadDocumentType(tab, item));
  }, [item, tab]);

  const filteredLeads = React.useMemo(() => {
    const q = leadSearch.trim().toLowerCase();
    if (!q) return leads.slice(0, 50);
    return leads.filter((lead) => leadDisplayLabel(lead).toLowerCase().includes(q)).slice(0, 50);
  }, [leadSearch, leads]);

  const filteredContacts = React.useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts.slice(0, 50);
    return contacts.filter((contact) => contactOptionLabel(contact).toLowerCase().includes(q)).slice(0, 50);
  }, [contactSearch, contacts]);

  const selectedLead = leads.find((lead) => String(lead.id) === draft.lead_id);
  const selectedContact = contacts.find((contact) => contactOptionLabel(contact) === draft.party_name);

  const uploadFiles = React.useCallback(async (files: File[]) => {
    if (files.length === 0 || cancelledRef.current || uploadInFlightRef.current) return;
    uploadInFlightRef.current = true;

    const { limited, toUpload, errors } = prepareInvoiceUploadFiles(files, tab);

    setPickedFiles(limited);
    setBatchErrors(errors);
    setError(null);
    setExtractionErrors({});
    uploadedItemsRef.current = [];
    savedIdsRef.current = new Set();
    setUploadedItems([]);
    setReviewIndex(0);

    if (toUpload.length === 0) {
      uploadInFlightRef.current = false;
      return;
    }

    setUploading(true);
    setUploadProgress({ completed: 0, total: toUpload.length });
    const extractionById: Record<string, string> = {};

    try {
      for (let i = 0; i < toUpload.length; i++) {
        if (cancelledRef.current) break;
        const file = toUpload[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', tab);
        try {
          const response = await fetch('/api/invoices/upload', { method: 'POST', body: formData });
          const payload = await response.json().catch(() => ({}));
          if (cancelledRef.current) break;
          if (!response.ok) {
            errors.push(payload?.error ? `${file.name}: ${payload.error}` : `${file.name}: Upload fehlgeschlagen (${response.status})`);
          } else if (payload.item) {
            uploadedItemsRef.current = [...uploadedItemsRef.current, payload.item];
            if (payload.extraction_error) {
              extractionById[payload.item.id] = payload.extraction_error;
            }
          } else {
            errors.push(`${file.name}: Upload fehlgeschlagen.`);
          }
        } catch {
          if (!cancelledRef.current) {
            errors.push(`${file.name}: Upload fehlgeschlagen (Netzwerkfehler)`);
          }
        }
        if (!cancelledRef.current) {
          setUploadProgress({ completed: i + 1, total: toUpload.length });
        }
      }

      if (cancelledRef.current) return;

      const items = uploadedItemsRef.current;
      setUploadedItems(items);
      setBatchErrors([...errors]);
      setExtractionErrors(extractionById);
      if (items.length > 0) {
        setReviewIndex(0);
        setError(extractionById[items[0].id] ?? null);
      }
    } finally {
      uploadInFlightRef.current = false;
      if (!cancelledRef.current) {
        setUploading(false);
        setUploadProgress(null);
      }
    }
  }, [tab]);

  const handlePickedFiles = React.useCallback((fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0 || uploading) return;
    void uploadFiles(files);
  }, [uploadFiles, uploading]);

  const handleClose = React.useCallback(async () => {
    cancelledRef.current = true;
    if (tab !== 'incoming') {
      const pending = uploadedItemsRef.current.filter(
        (row) => !savedIdsRef.current.has(row.id) && (row.status === 'draft' || row.status === 'extraction_pending'),
      );
      await Promise.all(
        pending.map((row) =>
          fetch(`/api/invoices/${encodeURIComponent(row.id)}`, { method: 'DELETE' }).catch(() => {
            // Best-effort cleanup; ignore failures so the user can always close the modal.
          }),
        ),
      );
    }
    onClose();
  }, [onClose, tab]);

  const saveReview = async () => {
    if (!item) return;
    setUploading(true);
    setError(null);
    const isCreditNote = documentType === 'credit_note';
    const isGutschrift = documentType === 'invoice_correction';
    const isIncomingCredit = isIncomingCreditDocumentType(documentType);
    const allocation = invoiceAuftragPatchFromSelect(draft.lead_id);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(item.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          due_date: isCreditNote || isGutschrift || isIncomingCredit ? '' : draft.due_date,
          // Eingang: bleibt in Prüfung (gebucht wird über „Freigeben“), Belegwährung bleibt erhalten.
          currency: tab === 'incoming' ? undefined : 'EUR',
          status: tab === 'incoming' ? undefined : item.duplicate_of ? 'draft' : isCreditNote ? 'paid' : 'open',
          paid_at: isCreditNote ? new Date().toISOString().slice(0, 10) : undefined,
          lead_id: allocation.lead_id,
          is_overhead: tab === 'incoming' ? allocation.is_overhead : false,
          document_type: tab === 'incoming'
            ? (isIncomingCredit ? INCOMING_CREDIT_DOCUMENT_TYPE : INCOMING_INVOICE_DOCUMENT_TYPE)
            : documentType,
          organization_id: tab === 'incoming' ? (draft.organization_id || null) : undefined,
          reference_id: draft.invoice_number || null,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Rechnung konnte nicht gespeichert werden.');
      savedIdsRef.current.add(item.id);
      onSaved();
      const nextIndex = reviewIndex + 1;
      if (nextIndex < uploadedItems.length) {
        setReviewIndex(nextIndex);
        setError(extractionErrors[uploadedItems[nextIndex].id] ?? null);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rechnung konnte nicht gespeichert werden.');
    } finally {
      setUploading(false);
    }
  };

  const modalTitle = tab === 'quotes' ? 'Angebot hochladen' : tab === 'incoming' ? 'Eingangsrechnung hochladen' : 'Rechnung hochladen';
  const accept = tab === 'incoming' ? 'application/pdf,application/xml,text/xml,image/jpeg,image/png,image/webp,image/heic,image/heif,.xml,.pdf,.heic,.heif' : 'application/pdf';
  const dropzoneTitle = tab === 'incoming' ? 'PDF, XML oder Bild auswählen oder hier ablegen' : 'PDF auswählen oder hier ablegen';
  const dropzoneHint = tab === 'incoming'
    ? `Max. 20 MB pro Datei, bis zu ${MAX_INVOICE_UPLOAD_FILES} Dateien. PDF, XML oder Bild.`
    : `Max. 20 MB pro Datei, bis zu ${MAX_INVOICE_UPLOAD_FILES} Dateien, nur PDF.`;
  const pickedLabel = pickedFiles.length === 1
    ? pickedFiles[0].name
    : pickedFiles.length > 1
      ? `${pickedFiles.length} Dateien ausgewählt`
      : dropzoneTitle;
  const isLastReview = reviewIndex >= uploadedItems.length - 1;
  const saveLabel = isLastReview
    ? 'Rechnung speichern'
    : `Speichern & nächste (${reviewIndex + 2}/${uploadedItems.length})`;

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center bg-slate-900/45 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-xl bg-app-surface shadow-2xl border border-app-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border bg-app-surface-muted">
          <div>
            <h2 className="text-base font-semibold text-app-text">{modalTitle}</h2>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {tab === 'incoming'
                ? 'PDF, XML oder Bild hochladen (bis zu 10 Dateien), Auslese prüfen und speichern.'
                : 'PDF hochladen (bis zu 10 Dateien), KI-Extraktion prüfen und speichern.'}
            </p>
          </div>
          <button type="button" onClick={() => void handleClose()} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!item ? (
            <>
              <div
                className={`relative rounded-xl border-2 border-dashed text-center transition-colors ${
                  uploading
                    ? 'border-sky-400 bg-sky-50/60 cursor-default'
                    : isDragging
                      ? 'border-blue-400 bg-blue-50 cursor-pointer'
                      : 'border-app-border-strong bg-app-surface-muted hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!uploading) setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                  if (uploading) return;
                  handlePickedFiles(e.dataTransfer.files);
                }}
              >
                {uploading ? <DocumentUploadProgressOverlay progress={uploadProgress} /> : null}
                <label className={`block p-8 ${uploading ? 'invisible pointer-events-none' : 'cursor-pointer'}`}>
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-app-text">{pickedLabel}</p>
                  <p className="text-xs text-app-text-subtle mt-1">{dropzoneHint}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const list = e.target.files;
                      if (!list?.length) return;
                      handlePickedFiles(list);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {batchErrors.length > 0 && (
                <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    {batchErrors.map((message, index) => (
                      <p key={`${index}-${message}`}>{message}</p>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {uploadedItems.length > 1 && (
                <p className="text-xs font-medium text-app-text-subtle">
                  Rechnung {reviewIndex + 1} von {uploadedItems.length}
                </p>
              )}
              {item.duplicate_of ? (
                <IncomingInvoiceDuplicateBanner duplicateOf={item.duplicate_of} />
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Extraktion abgeschlossen. Bitte prüfe die Werte vor dem Speichern.</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative text-xs font-medium text-app-text-muted">
                  {tab === 'incoming' ? 'Auftrag' : 'Projekt optional'}
                  {tab === 'incoming' ? (
                    <div className="mt-1">
                      <AppSelectDropdown
                        value={draft.lead_id}
                        onChange={(value) => {
                          setDraft((prev) => ({ ...prev, lead_id: value }));
                        }}
                        options={incomingAuftragSelectOptions(leads)}
                        placeholder="Auftrag zuordnen"
                        searchable
                        searchPlaceholder="Projekte suchen…"
                        triggerClassName="!min-h-0 h-9 !py-0 w-full"
                        menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                      />
                    </div>
                  ) : (
                    <>
                  <button type="button" onClick={() => setLeadOpen((open) => !open)} className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-left text-sm font-normal text-app-text">
                    {selectedLead ? leadDisplayLabel(selectedLead) : 'Projekt auswählen'}
                  </button>
                  {leadOpen && (
                    <InlineSelectPopover
                      search={leadSearch}
                      onSearch={setLeadSearch}
                      items={filteredLeads}
                      renderItem={leadDisplayLabel}
                      onSelect={(lead) => {
                        setDraft((prev) => ({
                          ...prev,
                          lead_id: String(lead.id),
                          party_name: prev.party_name || lead.name || '',
                        }));
                        setLeadOpen(false);
                        setLeadSearch('');
                      }}
                      onCreate={onCreateLead ? () => {
                        setLeadOpen(false);
                        onCreateLead();
                      } : undefined}
                      onClose={() => setLeadOpen(false)}
                      createLabel="+ Neues Projekt erstellen"
                      placeholder="Projekte suchen..."
                    />
                  )}
                    </>
                  )}
                </div>
                <div className="relative text-xs font-medium text-app-text-muted">
                  {tab === 'incoming' ? 'Lieferant' : 'Kontakt / Lieferant'}
                  {tab === 'incoming' ? (
                    <div className="mt-1">
                      <AppSelectDropdown
                        value={draft.organization_id}
                        onChange={(value) => {
                          const org = organizations.find((org) => org.id === value);
                          setDraft((prev) => ({
                            ...prev,
                            organization_id: value,
                            party_name: org?.name || prev.party_name,
                          }));
                        }}
                        options={[
                          { value: '', label: 'Kein Lieferant' },
                          ...organizations
                            .filter((org) => !org.organization_type || org.organization_type === 'Lieferant')
                            .map((org) => ({ value: org.id, label: org.name })),
                        ]}
                        placeholder="Lieferant auswählen"
                        searchable
                        triggerClassName="!min-h-0 h-9 !py-0 w-full"
                      />
                      <input
                        type="text"
                        value={draft.party_name}
                        onChange={(e) => setDraft((prev) => ({ ...prev, party_name: e.target.value }))}
                        placeholder="Name auf der Rechnung"
                        className="mt-2 w-full h-9 rounded-md border border-app-border px-3 text-sm font-normal text-app-text"
                      />
                    </div>
                  ) : (
                    <>
                  <button type="button" onClick={() => setContactOpen((open) => !open)} className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-left text-sm font-normal text-app-text">
                    {selectedContact ? contactOptionLabel(selectedContact) : draft.party_name || 'Kontakt auswählen'}
                  </button>
                  {contactOpen && (
                    <InlineSelectPopover
                      search={contactSearch}
                      onSearch={setContactSearch}
                      items={filteredContacts}
                      renderItem={contactOptionLabel}
                      onSelect={(contact) => {
                        setDraft((prev) => ({ ...prev, party_name: contactDisplayName(contact) || contact.company || contact.name || '' }));
                        setContactOpen(false);
                        setContactSearch('');
                      }}
                      onCreate={onCreateContact ? () => {
                        setContactOpen(false);
                        onCreateContact();
                      } : undefined}
                      onClose={() => setContactOpen(false)}
                      createLabel="+ Neuen Kontakt erstellen"
                      placeholder="Kontakte suchen..."
                    />
                  )}
                    </>
                  )}
                </div>
                {tab === 'outgoing' && (
                  <div className="col-span-2 text-xs font-medium text-app-text-muted">
                    Dokumenttyp
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(
                        [
                          { id: 'invoice', label: 'Rechnung' },
                          { id: 'partial_invoice', label: 'Abschlagsrechnung' },
                          { id: 'final_invoice', label: 'Schlussrechnung' },
                          { id: 'credit_note', label: 'Stornorechnung' },
                          { id: 'invoice_correction', label: 'Gutschrift' },
                        ] as { id: InvoiceDocument['document_type']; label: string }[]
                      ).map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setDocumentType(id)}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                            documentType === id
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {tab === 'incoming' && (
                  <div className="col-span-2 text-xs font-medium text-app-text-muted">
                    Belegart
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(
                        [
                          { id: INCOMING_INVOICE_DOCUMENT_TYPE, label: 'Rechnung' },
                          { id: INCOMING_CREDIT_DOCUMENT_TYPE, label: 'Gutschrift' },
                        ] as { id: InvoiceDocument['document_type']; label: string }[]
                      ).map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setDocumentType(id)}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                            documentType === id
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {[
                  ['invoice_number', 'Rechnungsnummer'],
                  ['issue_date', 'Rechnungsdatum'],
                  ...(documentType !== 'credit_note' && documentType !== 'invoice_correction' && !isIncomingCreditDocumentType(documentType)
                    ? [['due_date', 'Fälligkeit'] as [string, string]]
                    : []),
                  ['gross_amount', 'Betrag'],
                ].map(([key, label]) => (
                  <label key={key} className="text-xs font-medium text-app-text-muted">
                    {label}
                    <input
                      type={key.includes('date') ? 'date' : 'text'}
                      value={(draft as any)[key]}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-app-border px-3 py-2 text-sm font-normal text-app-text"
                    />
                  </label>
                ))}
              </div>
              {batchErrors.length > 0 && (
                <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    {batchErrors.map((message, index) => (
                      <p key={`${index}-${message}`}>{message}</p>
                    ))}
                  </div>
                </div>
              )}
              {error && (
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-app-border bg-app-surface-muted">
          <button type="button" onClick={() => void handleClose()} className="rounded-lg border border-app-border bg-app-surface px-4 py-2 text-sm font-medium text-app-text-muted hover:bg-app-hover">
            Abbrechen
          </button>
          {!item ? (
            <button
              type="button"
              onClick={() => void uploadFiles(pickedFiles)}
              disabled={pickedFiles.length === 0 || uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading && <LoadingSpinner />}
              Hochladen & auslesen
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void saveReview()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading && <LoadingSpinner />}
              {saveLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function InvoicesPage() {
  const { can } = useCapabilities();
  // Projekte und Kontakte sind eigene Module: nur laden und anlegen, wenn das Recht da ist.
  const canViewProjects = can('web.projekte.view');
  const canCreateProjects = can('web.projekte.edit');
  const canViewContacts = can('web.kontakte.view');
  const canCreateContacts = can('web.kontakte.edit');
  const { instanceId, tabSlug } = useParams<{ instanceId: string; tabSlug: string }>();
  const navigate = useNavigate();
  const tabFromUrl = invoiceTabFromSlug(tabSlug);
  const tab = tabFromUrl ?? 'outgoing';
  // Der Reiter „Angebote“ zeigt gesendete Angebote und gehört zum Modul Angebote.
  const { loaded: modulesLoaded, isEnabled: isModuleOn } = useModuleAccess();
  const quotesTabAvailable = modulesLoaded && isModuleOn('angebote');
  React.useEffect(() => {
    if (modulesLoaded && tab === 'quotes' && !isModuleOn('angebote')) {
      navigate(invoicesOverviewPath(instanceId, 'outgoing'), { replace: true });
    }
  }, [modulesLoaded, tab, isModuleOn, navigate, instanceId]);

  // ?status=draft (z. B. Link „Zur Freigabe“) filtert direkt auf diesen Status.
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status');
  const initialStatusesForTab = React.useCallback(
    (forTab: InvoiceTab) =>
      statusFromUrl && INVOICE_STATUS_FILTER_KEYS.has(statusFromUrl)
        ? new Set([statusFromUrl])
        : defaultSelectedStatusesForTab(forTab),
    [statusFromUrl],
  );
  const [selectedStatuses, setSelectedStatuses] = React.useState<Set<string>>(() =>
    initialStatusesForTab(tabFromUrl ?? 'outgoing'),
  );
  const [retentionHeldFilter, setRetentionHeldFilter] = React.useState(false);
  const [sort, setSort] = React.useState<InvoiceSortColumn>('due_date');
  const [dir, setDir] = React.useState<'asc' | 'desc'>('asc');
  const [query, setQuery] = React.useState('');
  const [rows, setRows] = React.useState<InvoiceDocument[]>([]);
  /** Eingangsrechnungen in Prüfung (Zähler am Reiter) und ab wann ein Beleg als liegengeblieben gilt. */
  const [reviewCount, setReviewCount] = React.useState(0);
  const [reviewStaleDays, setReviewStaleDays] = React.useState(10);
  const [kpiRows, setKpiRows] = React.useState<InvoiceDocument[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [datevOpen, setDatevOpen] = React.useState(false);
  const datevStatus = useDatevStatus();
  const [previewRow, setPreviewRow] = React.useState<InvoiceDocument | null>(null);
  const [editingCell, setEditingCell] = React.useState<{ rowId: string; field: EditableInvoiceField } | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const [savingCell, setSavingCell] = React.useState(false);
  const [completeProjectPrompt, setCompleteProjectPrompt] = React.useState<{
    leadId: string;
    projectLabel: string;
    stageId: string | null;
  } | null>(null);
  const [completingProject, setCompletingProject] = React.useState(false);
  const [leads, setLeads] = React.useState<LeadOption[]>([]);
  const [contacts, setContacts] = React.useState<ContactOption[]>([]);
  const [organizations, setOrganizations] = React.useState<OrganizationOption[]>([]);
  const [leadSearch, setLeadSearch] = React.useState('');
  const [contactSearch, setContactSearch] = React.useState('');
  const [orgSearch, setOrgSearch] = React.useState('');
  const [createLeadOpen, setCreateLeadOpen] = React.useState(false);
  const [createContactOpen, setCreateContactOpen] = React.useState(false);
  const [createSupplierFor, setCreateSupplierFor] = React.useState<{ row: InvoiceDocument; name: string } | null>(null);
  const addOrganizationOption = React.useCallback((org: Organization) => {
    setOrganizations((prev) => [
      {
        id: String(org.id),
        name: String(org.name ?? '').trim(),
        organization_type: String(org.organization_type ?? 'Lieferant'),
        iban: org.iban ?? null,
        bic: org.bic ?? null,
        bank_account_holder: org.bank_account_holder ?? null,
      },
      ...prev.filter((item) => item.id !== String(org.id)),
    ]);
  }, []);
  const [deleteTarget, setDeleteTarget] = React.useState<InvoiceDocument | null>(null);
  const [reclassifyTarget, setReclassifyTarget] = React.useState<InvoiceDocument | null>(null);
  const [reclassifyBusy, setReclassifyBusy] = React.useState(false);
  const [rejectPrompt, setRejectPrompt] = React.useState<{ row: InvoiceDocument; patch: Record<string, unknown> } | null>(null);
  const [rejectReasonDraft, setRejectReasonDraft] = React.useState<InvoiceRejectionReason>('not_invoice');
  const [filterYear, setFilterYear] = React.useState<number | null>(null);
  const [filterMonth, setFilterMonth] = React.useState<number | null>(null);
  const [periodOpen, setPeriodOpen] = React.useState(false);
  const periodButtonRef = React.useRef<HTMLButtonElement>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkBusy, setBulkBusy] = React.useState<'download' | 'delete' | 'sepa' | 'status' | 'approve' | null>(null);
  const [bulkError, setBulkError] = React.useState<string | null>(null);
  const [sepaOpen, setSepaOpen] = React.useState(false);
  const [sepaPreview, setSepaPreview] = React.useState<IncomingSepaPreview | null>(null);
  const [sepaPreviewLoading, setSepaPreviewLoading] = React.useState(false);
  const [sepaError, setSepaError] = React.useState<string | null>(null);
  const [sepaExecutionDate, setSepaExecutionDate] = React.useState(() => todayYMD());
  const sepaPreviewRequestRef = React.useRef(0);
  // Nach dem Export: Rechnungen, bei denen Skonto abgezogen wurde, optional als „gezogen“ markieren.
  const [sepaSkontoPrompt, setSepaSkontoPrompt] = React.useState<IncomingSepaPreviewItem[] | null>(null);
  const [sepaSkontoBusy, setSepaSkontoBusy] = React.useState(false);
  const headerCheckboxRef = React.useRef<HTMLInputElement>(null);
  // Die Tabelle ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const setTableScrollNode = useHorizontalWheelScroll<HTMLDivElement>();
  const [dunningBusy, setDunningBusy] = React.useState<{ rowId: string; stage: DunningStage } | null>(null);
  const [accountingForwardBusyId, setAccountingForwardBusyId] = React.useState<string | null>(null);
  const [accountingForwardEmailConfigured, setAccountingForwardEmailConfigured] = React.useState(false);
  const [constructionDeductionsEnabled, setConstructionDeductionsEnabled] = React.useState(false);
  const [dunningCompose, setDunningCompose] = React.useState<{
    rowId: string;
    stage: DunningStage;
    invoiceNumber: string | null;
    accountId: string | null;
    leadId: string | null;
    initialTo: string;
    initialCc: string;
    initialSubject: string;
    initialBody: string;
    initialAttachments: File[];
    signatureHtml?: string | null;
    fromEmail?: string | null;
  } | null>(null);
  const [dunningComposeKey, setDunningComposeKey] = React.useState(0);
  /** „Per E-Mail senden“ aus dem Zeilenmenü: Beleg-PDF als Anhang, ohne Statusänderung. */
  const [documentMail, setDocumentMail] = React.useState<{
    accountId: string | null;
    leadId: string | null;
    initialTo: string;
    initialCc: string;
    initialSubject: string;
    initialBody: string;
    initialAttachments: File[];
    signatureHtml: string | null;
    fromEmail: string | null;
  } | null>(null);
  const [documentMailKey, setDocumentMailKey] = React.useState(0);
  const [dunningNotice, setDunningNotice] = React.useState<{
    type: 'need_mail' | 'error';
    message?: string;
    /** Belegversand statt Mahnung: andere Überschrift und Hinweistext. */
    purpose?: 'dunning' | 'document';
  } | null>(null);
  const [mobileStatusRow, setMobileStatusRow] = React.useState<InvoiceDocument | null>(null);
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false,
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const selectedStatusesKey = React.useMemo(
    () => [...selectedStatuses].sort().join(','),
    [selectedStatuses],
  );

  const loadInvoicesRequestRef = React.useRef(0);
  const loadInvoicesLoadingRequestRef = React.useRef<number | null>(null);
  const loadKpiDataRequestRef = React.useRef(0);
  const reloadOverviewInFlightCountRef = React.useRef(0);
  const reloadOverviewPendingSilentRef = React.useRef(false);

  React.useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch('/api/settings/invoice-forwarding')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { accounting_forward_email?: string | null } | null) => {
        if (cancelled) return;
        setAccountingForwardEmailConfigured(Boolean(String(data?.accounting_forward_email ?? '').trim()));
      })
      .catch(() => {
        if (!cancelled) setAccountingForwardEmailConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  React.useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch('/api/settings/company', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { construction_deductions_enabled?: boolean } | null) => {
        if (!cancelled) {
          setConstructionDeductionsEnabled(data?.construction_deductions_enabled === true);
          const stale = Number((data as { incoming_review_stale_days?: number } | null)?.incoming_review_stale_days);
          setReviewStaleDays(Number.isFinite(stale) && stale >= 1 ? stale : 10);
        }
      })
      .catch(() => {
        if (!cancelled) setConstructionDeductionsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  React.useEffect(() => {
    if (!tabFromUrl) return;
    setSelectedStatuses(initialStatusesForTab(tabFromUrl));
    setRetentionHeldFilter(false);
    setSort('due_date');
    setDir('asc');
    setSelectedIds(new Set());
    setBulkError(null);
    setPage(0);
  }, [tabFromUrl]);

  const loadKpiData = React.useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    const requestId = ++loadKpiDataRequestRef.current;
    try {
      const allRows: InvoiceDocument[] = [];
      let currentPage = 1;
      let totalCount = 0;
      while (true) {
        const params = invoiceListQueryParams({
          tab,
          sort: 'issue_date',
          dir: 'desc',
          page: currentPage,
          perPage: INVOICE_PAGE_SIZE,
          filterYear,
          filterMonth,
          forKpis: true,
        });
        const response = await fetch(`/api/invoices?${params.toString()}`);
        const payload = (await response.json().catch(() => ({}))) as Partial<InvoiceListResponse> & { error?: string };
        if (!response.ok) {
          if (!silent) setKpiRows([]);
          return;
        }
        const items = Array.isArray(payload.items) ? payload.items : [];
        if (items.length === 0) break;
        allRows.push(...items);
        totalCount = Number(payload.total ?? allRows.length);
        if (allRows.length >= totalCount) break;
        currentPage += 1;
      }
      if (requestId !== loadKpiDataRequestRef.current) return;
      setKpiRows(allRows);
    } catch {
      if (!silent) setKpiRows([]);
    }
  }, [tab, filterYear, filterMonth]);

  const patchInvoiceRowState = React.useCallback((rowId: string, patch: Partial<InvoiceDocument>) => {
    setRows((prev) => prev.map((item) => (item.id === rowId ? { ...item, ...patch } : item)));
    setKpiRows((prev) => prev.map((item) => (item.id === rowId ? { ...item, ...patch } : item)));
    setPreviewRow((prev) => (prev && prev.id === rowId ? { ...prev, ...patch } : prev));
  }, []);

  React.useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch('/api/invoices/review-count', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { count?: number } | null) => {
        if (!cancelled) setReviewCount(Number(data?.count ?? 0) || 0);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [instanceId, rows]);

  const loadInvoices = React.useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    const requestId = ++loadInvoicesRequestRef.current;
    if (selectedStatuses.size === 0) {
      if (requestId !== loadInvoicesRequestRef.current) return;
      setRows([]);
      setTotal(0);
      if (!silent && loadInvoicesLoadingRequestRef.current === requestId) {
        loadInvoicesLoadingRequestRef.current = null;
        setLoading(false);
      }
      return;
    }
    if (!silent) {
      loadInvoicesLoadingRequestRef.current = requestId;
      setLoading(true);
      setError(null);
    }
    try {
      const params = invoiceListQueryParams({
        tab,
        sort,
        dir,
        page: page + 1,
        perPage: INVOICE_PAGE_SIZE,
        query,
        selectedStatuses,
        retentionHeld: retentionHeldFilter,
        filterYear,
        filterMonth,
      });
      const response = await fetch(`/api/invoices?${params.toString()}`);
      const payload = (await response.json().catch(() => ({}))) as Partial<InvoiceListResponse> & { error?: string };
      if (!response.ok) throw new Error(payload.error || 'Rechnungen konnten nicht geladen werden.');
      if (requestId !== loadInvoicesRequestRef.current) return;
      setRows(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total ?? payload.items?.length ?? 0));
    } catch (err) {
      if (requestId !== loadInvoicesRequestRef.current) return;
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Rechnungen konnten nicht geladen werden.');
      }
    } finally {
      if (!silent && loadInvoicesLoadingRequestRef.current === requestId) {
        loadInvoicesLoadingRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [tab, sort, dir, query, page, selectedStatusesKey, retentionHeldFilter, filterYear, filterMonth]);

  React.useEffect(() => {
    const maxPage = total === 0 ? 0 : Math.max(0, Math.ceil(total / INVOICE_PAGE_SIZE) - 1);
    setPage((current) => Math.min(current, maxPage));
  }, [total]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => { void loadInvoices(); }, 180);
    return () => window.clearTimeout(timeout);
  }, [loadInvoices]);

  React.useEffect(() => {
    void loadKpiData();
  }, [loadKpiData]);

  React.useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Über Refs immer die aktuellen Loader nutzen: nach Tab-Wechsel (z. B. Umwandlung) darf ein
  // nachgeholter Reload nicht mehr mit dem alten Tab laden und die Liste überschreiben.
  const loadInvoicesRef = React.useRef(loadInvoices);
  loadInvoicesRef.current = loadInvoices;
  const loadKpiDataRef = React.useRef(loadKpiData);
  loadKpiDataRef.current = loadKpiData;

  const reloadInvoiceOverview = React.useCallback((options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (silent && reloadOverviewInFlightCountRef.current > 0) {
      reloadOverviewPendingSilentRef.current = true;
      return;
    }

    reloadOverviewInFlightCountRef.current += 1;
    void Promise.all([loadInvoicesRef.current(options), loadKpiDataRef.current(options)]).finally(() => {
      reloadOverviewInFlightCountRef.current -= 1;
      if (reloadOverviewInFlightCountRef.current === 0 && reloadOverviewPendingSilentRef.current) {
        reloadOverviewPendingSilentRef.current = false;
        reloadInvoiceOverview({ silent: true });
      }
    });
  }, []);

  useInvoiceDocumentsLive({
    instanceId: instanceId ?? null,
    onRefresh: () => reloadInvoiceOverview({ silent: true }),
  });

  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        reloadInvoiceOverview({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reloadInvoiceOverview]);

  const loadLookupData = React.useCallback(async () => {
    const [leadResponse, contactResponse, orgResponse] = await Promise.all([
      canViewProjects ? fetch('/api/leads') : null,
      canViewContacts ? fetch('/api/contacts') : null,
      canViewContacts ? fetch('/api/organizations') : null,
    ]);
    const [leadPayload, contactPayload, orgPayload] = await Promise.all([
      leadResponse?.ok ? leadResponse.json() : [],
      contactResponse?.ok ? contactResponse.json() : [],
      orgResponse?.ok ? orgResponse.json() : [],
    ]);
    setLeads(Array.isArray(leadPayload) ? leadPayload : []);
    setContacts(Array.isArray(contactPayload) ? contactPayload : []);
    setOrganizations(
      Array.isArray(orgPayload)
        ? orgPayload
            .map((o: {
              id?: string;
              name?: string;
              organization_type?: string;
              iban?: string | null;
              bic?: string | null;
              bank_account_holder?: string | null;
            }) => ({
              id: String(o.id ?? ''),
              name: String(o.name ?? '').trim(),
              organization_type: String(o.organization_type ?? ''),
              iban: o.iban ?? null,
              bic: o.bic ?? null,
              bank_account_holder: o.bank_account_holder ?? null,
            }))
            .filter((o) => o.id && o.name)
        : [],
    );
  }, [canViewProjects, canViewContacts]);

  React.useEffect(() => {
    void loadLookupData().catch(() => {
      setLeads([]);
      setContacts([]);
      setOrganizations([]);
    });
  }, [loadLookupData]);

  const partyDisplayContext = React.useMemo(() => {
    const leadsById = new Map<string, Pick<Lead, 'contact' | 'contact_id' | 'name'>>();
    for (const lead of leads) {
      leadsById.set(String(lead.id), lead);
    }
    const organizationNameById = new Map<string, string>();
    for (const org of organizations) {
      organizationNameById.set(org.id, org.name);
    }
    return { leadsById, contacts, organizationNameById };
  }, [leads, contacts, organizations]);

  const toggleSort = (key: InvoiceSortColumn) => {
    setPage(0);
    if (sort === key) setDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(key);
      setDir('desc');
    }
  };

  const availableYears = React.useMemo(() => {
    const set = new Set<number>();
    for (const row of kpiRows) {
      if (!row.issue_date) continue;
      const date = new Date(row.issue_date);
      if (!Number.isNaN(date.getTime())) set.add(date.getFullYear());
    }
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) set.add(y);
    return [...set].sort((a, b) => b - a);
  }, [kpiRows]);

  const periodLabel = React.useMemo(() => {
    if (filterYear == null && filterMonth == null) return 'Zeitraum';
    if (filterYear != null && filterMonth != null) {
      return `${MONTH_LABELS_SHORT[filterMonth - 1]} ${filterYear}`;
    }
    if (filterYear != null) return String(filterYear);
    return MONTH_LABELS[(filterMonth as number) - 1];
  }, [filterYear, filterMonth]);

  const periodActive = filterYear != null || filterMonth != null;

  const displayedRows = rows;
  const pageCount = total === 0 ? 0 : Math.ceil(total / INVOICE_PAGE_SIZE);
  const rangeFrom = total === 0 ? 0 : page * INVOICE_PAGE_SIZE + 1;
  const rangeTo = Math.min((page + 1) * INVOICE_PAGE_SIZE, total);

  // Abgelehnte Belege (z. B. nach Umklassifizierung in eine Eingangsrechnung) sind kein Umsatz —
  // sie tauchen in der Ausgangsliste auch in keinem Statusfilter auf.
  const revenueRows = kpiRows.filter(
    (row) => row.status !== 'cancelled' && row.status !== 'rejected' && row.document_type !== 'credit_note',
  );
  const receivableRows = revenueRows.filter((row) => row.document_type !== 'invoice_correction');
  const openRows = receivableRows.filter((row) => row.status === 'open' || row.status === 'overdue');
  const overdueRows = receivableRows.filter((row) => row.status === 'overdue');
  const paidRows = receivableRows.filter((row) => row.status === 'paid');
  const openSum = openRows.reduce((sum, row) => {
    const payable = toNumber(row.payable_amount);
    return sum + (payable > 0 ? payable : toNumber(row.gross_amount));
  }, 0);
  const overdueSum = overdueRows.reduce((sum, row) => {
    const payable = toNumber(row.payable_amount);
    return sum + (payable > 0 ? payable : toNumber(row.gross_amount));
  }, 0);
  // Eingegangenes Geld: offener Einbehalt und gezogenes Skonto sind nicht gezahlt.
  const paidSum = paidRows.reduce((sum, row) => sum + invoiceReceivedAmount(row), 0);
  const totalSum = revenueRows.reduce((sum, row) => sum + toNumber(row.gross_amount), 0);
  const itemLabelPlural = tab === 'quotes' ? 'Angebote' : 'Rechnungen';
  const totalCountLabel = revenueRows.length.toLocaleString('de-DE');
  const kpiCards = [
    { label: tab === 'incoming' ? 'Gesamtbetrag' : 'Gesamtumsatz', value: formatMoney(totalSum), meta: `${totalCountLabel} ${itemLabelPlural}`, icon: <Receipt className="w-4 h-4" />, color: 'text-blue-700 bg-blue-50', accent: 'border-t-blue-500' },
    { label: 'Offen', value: formatMoney(openSum), meta: `${openRows.filter((row) => row.status === 'open').length} ${itemLabelPlural}`, icon: <ArrowUpRight className="w-4 h-4" />, color: 'text-blue-700 bg-blue-50', accent: 'border-t-blue-500' },
    { label: 'Überfällig', value: formatMoney(overdueSum), meta: `${overdueRows.length} ${itemLabelPlural}`, icon: <AlertCircle className="w-4 h-4" />, color: 'text-rose-700 bg-rose-50', accent: 'border-t-rose-500' },
    { label: tab === 'quotes' ? 'Angenommen' : 'Bezahlt', value: formatMoney(paidSum), meta: `${paidRows.length} ${itemLabelPlural}`, icon: <CheckCircle className="w-4 h-4" />, color: 'text-cyan-700 bg-cyan-50', accent: 'border-t-cyan-500' },
  ];
  const searchPlaceholder =
    tab === 'quotes'
      ? 'Angebote, Kunde, Organisation …'
      : tab === 'incoming'
        ? 'Eingangsrechnungen, Lieferant …'
        : 'Rechnungen, Kunde, Organisation …';
  const paidColumnLabel = tab === 'quotes' ? 'Angenommen am' : 'Bezahlt am';
  const showDunningColumn = tab === 'outgoing';
  // Ohne Buchhaltungs-Mail bleibt die Spalte versteckt, außer die Liste enthält Sage-Belege:
  // deren Übergabe wird nur vermerkt, nicht versendet.
  const hasSageImportRows = rows.some((row) => row.source === 'sage_import');
  const showAccountingForwardColumn = tab === 'outgoing' && (accountingForwardEmailConfigured || hasSageImportRows);
  const showSupplierColumn = tab === 'incoming';
  const showRetentionColumn =
    tab === 'outgoing' &&
    (retentionHeldFilter ||
      rows.some((row) => toNumber(row.retention_gross) > 0) ||
      kpiRows.some((row) => toNumber(row.retention_gross) > 0 && row.retention_status === 'held'));
  const showRetentionFilter =
    tab === 'outgoing' &&
    (constructionDeductionsEnabled ||
      rows.some((row) => toNumber(row.retention_gross) > 0) ||
      kpiRows.some((row) => toNumber(row.retention_gross) > 0 && row.retention_status === 'held'));

  const tableColumns = React.useMemo(
    (): Array<{ id: string; label: string; sortKey?: InvoiceSortColumn; align?: 'left' | 'right' }> => [
      { id: 'nr', label: 'Nr.', sortKey: 'invoice_number' },
      { id: 'lead', label: 'Auftrag', sortKey: 'lead_display_id' },
      { id: 'customer', label: 'Kunde', sortKey: tab === 'incoming' ? undefined : 'party_name' },
      ...(showSupplierColumn ? [{ id: 'supplier', label: 'Lieferant', sortKey: 'party_name' as const }] : []),
      { id: 'issue_date', label: 'Rechnungsdatum', sortKey: 'issue_date' },
      { id: 'due_date', label: 'Fälligkeit', sortKey: 'due_date' },
      { id: 'paid_at', label: paidColumnLabel, sortKey: 'paid_at' },
      ...(showAccountingForwardColumn ? [{ id: 'accounting', label: 'Buchhaltung', sortKey: 'accounting_forwarded_at' as const }] : []),
      { id: 'amount', label: 'Betrag', sortKey: 'gross_amount', align: 'right' },
      ...(showRetentionColumn ? [{ id: 'retention', label: 'Einbehalt', align: 'right' as const }] : []),
      { id: 'status', label: 'Status', sortKey: 'status' },
      ...(showDunningColumn ? [{ id: 'dunning', label: 'Mahnstufe', sortKey: 'dunning_stage' as const }] : []),
      { id: 'actions', label: '', align: 'right' },
    ],
    [paidColumnLabel, showAccountingForwardColumn, showDunningColumn, showRetentionColumn, showSupplierColumn, tab],
  );

  const isAccountingForwardableType = (documentType: InvoiceDocument['document_type']) =>
    ['invoice', 'partial_invoice', 'final_invoice', 'credit_note', 'invoice_correction'].includes(documentType);
  const uploadActionLabel =
    tab === 'quotes'
      ? 'Angebot hochladen'
      : tab === 'incoming'
        ? 'Eingangsrechnung hochladen'
        : 'Rechnung hochladen';

  const maybeOfferCompleteProject = React.useCallback(async (
    row: InvoiceDocument,
    previousStatus: string,
    nextStatus: string,
  ) => {
    if (!shouldOfferCompleteProjectOnPaid({
      documentType: row.document_type,
      nextStatus,
      previousStatus,
      leadId: row.lead_id,
    })) {
      return;
    }
    if (!canViewProjects) return;
    const leadId = String(row.lead_id);
    try {
      const leadRes = await fetch(`/api/leads/${encodeURIComponent(leadId)}`, { credentials: 'include' });
      if (!leadRes.ok) return;
      const lead = (await leadRes.json()) as {
        outcome?: unknown;
        status?: string | null;
        stage_id?: string | null;
        lead_title?: string | null;
        display_id?: string | null;
        name?: string | null;
      };
      const outcome = isLeadOutcome(lead.outcome) ? lead.outcome : outcomeFromStatus(lead.status);
      if (outcome === 'won') return;
      const title = (lead.lead_title || row.lead_title || lead.name || '').trim();
      const displayId = String(lead.display_id || row.lead_display_id || '').trim();
      const projectLabel = [displayId, title].filter(Boolean).join(' · ') || 'dieses Projekt';
      setCompleteProjectPrompt({
        leadId,
        projectLabel,
        stageId: lead.stage_id != null ? String(lead.stage_id) : null,
      });
    } catch {
      // Rechnung ist bereits bezahlt; ohne Lead-Daten keine Abfrage anzeigen.
    }
  }, [canViewProjects]);

  const confirmCompleteProject = React.useCallback(async () => {
    if (!completeProjectPrompt || completingProject) return;
    setCompletingProject(true);
    setError(null);
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(completeProjectPrompt.leadId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          outcome: 'won',
          stage_id: completeProjectPrompt.stageId ?? undefined,
        }),
      });
      const payload = await res.json().catch(() => ({} as { error?: string }));
      if (!res.ok) throw new Error(payload?.error || 'Projekt konnte nicht abgeschlossen werden.');
      setCompleteProjectPrompt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekt konnte nicht abgeschlossen werden.');
    } finally {
      setCompletingProject(false);
    }
  }, [completeProjectPrompt, completingProject]);

  const startEditing = (row: InvoiceDocument, field: EditableInvoiceField) => {
    if (field === 'status' && isInvoiceStatusLockedDuringExtraction(row.status)) return;
    setEditingCell({ rowId: row.id, field });
    setEditValue(getEditableValue(row, field, tab));
    if (field === 'organization_id') {
      // Nicht zugeordnet: mit dem Namen aus dem Beleg vorsuchen.
      setOrgSearch(isUnassignedIncomingSupplier(row) ? String(row.party_name ?? '').trim() : '');
    }
  };

  const saveEditing = async (valueOverride?: string) => {
    if (!editingCell || savingCell) return;
    const row = rows.find((item) => item.id === editingCell.rowId);
    if (!row) return;
    const nextValue = (valueOverride ?? editValue).trim();
    if (nextValue === getEditableValue(row, editingCell.field, tab)) {
      setEditingCell(null);
      return;
    }
    if (editingCell.field === 'status' && nextValue === 'rejected') {
      // Grund abfragen (Dialog speichert über updateInvoiceRow).
      void updateInvoiceRow(row, { status: nextValue });
      return;
    }

    setSavingCell(true);
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(row.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [editingCell.field]: nextValue }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Eintrag konnte nicht aktualisiert werden.');
      const field = editingCell.field;
      patchInvoiceRowState(row.id, payload as Partial<InvoiceDocument>);
      setEditingCell(null);
      if (field === 'status') {
        void maybeOfferCompleteProject(row, row.status, nextValue);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eintrag konnte nicht aktualisiert werden.');
    } finally {
      setSavingCell(false);
    }
  };

  const cancelEditing = () => {
    if (savingCell) return;
    setEditingCell(null);
    setEditValue('');
    setLeadSearch('');
    setContactSearch('');
    setOrgSearch('');
  };

  const openInvoicePreview = async (invoiceId: string) => {
    const existing = rows.find((item) => item.id === invoiceId);
    if (existing) {
      setPreviewRow(existing);
      return;
    }
    setSavingCell(true);
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(invoiceId)}`, { credentials: 'include' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Rechnung konnte nicht geladen werden.');
      setPreviewRow(payload as InvoiceDocument);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rechnung konnte nicht geladen werden.');
    } finally {
      setSavingCell(false);
    }
  };

  const updateInvoiceRow = async (row: InvoiceDocument, patch: Record<string, unknown>): Promise<boolean> => {
    // Ablehnen eines Eingangsbelegs: Grund abfragen, bevor gespeichert wird.
    if (
      patch.status === 'rejected'
      && patch.rejection_reason === undefined
      && row.status !== 'rejected'
      && invoiceTabForDocument(row) === 'incoming'
    ) {
      setRejectReasonDraft('not_invoice');
      setRejectPrompt({ row, patch });
      setEditingCell(null);
      return false;
    }
    setSavingCell(true);
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(row.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Eintrag konnte nicht aktualisiert werden.');
      patchInvoiceRowState(row.id, payload as Partial<InvoiceDocument>);
      setEditingCell(null);
      setLeadSearch('');
      setContactSearch('');
      setOrgSearch('');
      if (typeof patch.status === 'string') {
        void maybeOfferCompleteProject(row, row.status, patch.status);
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eintrag konnte nicht aktualisiert werden.');
      return false;
    } finally {
      setSavingCell(false);
    }
  };

  const reclassifyInvoiceRow = async (row: InvoiceDocument): Promise<boolean> => {
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(row.id)}/reclassify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Beleg konnte nicht umgewandelt werden.');
      const item = payload?.item as InvoiceDocument | undefined;
      patchInvoiceRowState(row.id, { status: 'rejected', rejection_reason: 'reclassified', reclassified_to_id: item?.id ?? null });
      const nextTab = item?.id ? invoiceTabForDocument(item) : tab;
      if (item?.id) setPreviewRow(item);
      // Tab-Wechsel lädt die Liste selbst neu; ein Reload hier liefe noch mit dem alten Tab.
      if (instanceId && nextTab !== tab) navigate(invoicesOverviewPath(instanceId, nextTab));
      else reloadInvoiceOverview({ silent: true });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Beleg konnte nicht umgewandelt werden.');
      return false;
    }
  };

  const handleOfferDeleted = (row: InvoiceDocument) => {
    setRows((prev) => prev.filter((item) => item.id !== row.id));
    setKpiRows((prev) => prev.filter((item) => item.id !== row.id));
    setTotal((prev) => Math.max(0, prev - 1));
    setSelectedIds((prev) => {
      if (!prev.has(row.id)) return prev;
      const next = new Set(prev);
      next.delete(row.id);
      return next;
    });
  };

  const bulkSelectEnabled = tab === 'outgoing' || tab === 'quotes' || tab === 'incoming';
  const bulkDeleteEnabled = tab === 'quotes';
  const visibleSelectedIds = React.useMemo(() => {
    if (!bulkSelectEnabled) return [] as string[];
    return displayedRows.filter((row) => selectedIds.has(row.id)).map((row) => row.id);
  }, [bulkSelectEnabled, displayedRows, selectedIds]);
  // Sammelaktionen nur anbieten, wenn sie für die Auswahl gelten. Eingang: gebucht wird über
  // „Freigeben“, nicht über den Status; „Überfällig“ und „Fehler“ setzt das System selbst.
  const selectedRowsOnPage = React.useMemo(
    () => displayedRows.filter((row) => selectedIds.has(row.id)),
    [displayedRows, selectedIds],
  );
  const selectedInReviewCount = tab === 'incoming'
    ? selectedRowsOnPage.filter((row) => row.status === 'draft' && !row.approved_at && row.source !== 'sage_import').length
    : 0;
  const bulkStatusOptions = React.useMemo(() => {
    const base = statusOptionsForTab(tab);
    if (tab !== 'incoming') return base;
    return base.filter((option) =>
      !['draft', 'overdue', 'extraction_failed'].includes(option.id)
      && selectedRowsOnPage.every((row) =>
        statusOptionsForRow(tab, row).some((allowed) => allowed.id === option.id)
        && (row.source !== 'sage_import' || isSageImportPaymentStatus(option.id, row.document_type)),
      ),
    );
  }, [tab, selectedRowsOnPage]);
  const allVisibleSelected = bulkSelectEnabled && displayedRows.length > 0 && visibleSelectedIds.length === displayedRows.length;
  const someVisibleSelected = visibleSelectedIds.length > 0 && !allVisibleSelected;

  React.useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const row of displayedRows) next.delete(row.id);
        return next;
      }
      const next = new Set(prev);
      for (const row of displayedRows) next.add(row.id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Beträge hängen vom Ausführungsdatum ab (Skontofrist), daher bei jeder Datumsänderung neu laden.
  const loadSepaPreview = async (ids: string[], executionDate: string) => {
    const requestId = ++sepaPreviewRequestRef.current;
    setSepaPreviewLoading(true);
    setSepaError(null);
    try {
      const response = await fetch('/api/invoices/sepa-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, execution_date: executionDate }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Vorschau fehlgeschlagen.');
      if (requestId === sepaPreviewRequestRef.current) setSepaPreview(payload as IncomingSepaPreview);
    } catch (err) {
      if (requestId !== sepaPreviewRequestRef.current) return;
      setSepaError(err instanceof Error ? err.message : 'Vorschau fehlgeschlagen.');
      setSepaPreview({ company: { name: '', iban: null, bic: null, hasIban: false }, items: [] });
    } finally {
      if (requestId === sepaPreviewRequestRef.current) setSepaPreviewLoading(false);
    }
  };

  const openSepaPreview = async () => {
    if (tab !== 'incoming' || selectedIds.size === 0 || bulkBusy) return;
    const today = todayYMD();
    setSepaOpen(true);
    setSepaExecutionDate(today);
    await loadSepaPreview(Array.from(selectedIds), today);
  };

  const changeSepaExecutionDate = (value: string) => {
    setSepaExecutionDate(value);
    if (value && sepaPreview && sepaPreview.items.length > 0) {
      void loadSepaPreview(sepaPreview.items.map((item) => item.id), value);
    }
  };

  const markSepaSkontoTaken = async () => {
    if (!sepaSkontoPrompt) return;
    setSepaSkontoBusy(true);
    try {
      const results = await Promise.all(sepaSkontoPrompt.map((item) =>
        fetch(`/api/invoices/${encodeURIComponent(item.id)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skonto_taken: true }),
        }).then((response) => response.ok).catch(() => false),
      ));
      const failed = results.filter((ok) => !ok).length;
      if (failed > 0) {
        setBulkError(`Skonto konnte bei ${failed} von ${results.length} Rechnungen nicht markiert werden.`);
      }
      setSepaSkontoPrompt(null);
      void loadInvoices({ silent: true });
    } finally {
      setSepaSkontoBusy(false);
    }
  };

  const exportSepaFile = async () => {
    if (!sepaPreview || bulkBusy) return;
    setBulkBusy('sepa');
    setSepaError(null);
    try {
      const response = await fetch('/api/invoices/sepa-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: sepaPreview.items.filter((item) => item.valid).map((item) => item.id),
          execution_date: sepaExecutionDate,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload?.error || `SEPA-Export fehlgeschlagen (${response.status}).`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sepa_eingangsrechnungen_${sepaExecutionDate}.xml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSepaOpen(false);
      const withSkonto = sepaPreview.items.filter((item) => item.valid && item.skonto_applied > 0);
      if (withSkonto.length > 0) setSepaSkontoPrompt(withSkonto);
    } catch (err) {
      setSepaError(err instanceof Error ? err.message : 'SEPA-Export fehlgeschlagen.');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkStatus = async (nextStatus: string) => {
    if (selectedIds.size === 0 || bulkBusy || !nextStatus) return;
    setBulkBusy('status');
    setBulkError(null);
    try {
      const response = await fetch('/api/invoices/bulk-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({} as { error?: string; updated?: InvoiceDocument[]; skipped?: number }));
      if (!response.ok) throw new Error(payload?.error || `Status konnte nicht geändert werden (${response.status}).`);
      for (const row of payload.updated ?? []) {
        patchInvoiceRowState(row.id, row);
      }
      const skipped = Number(payload.skipped ?? 0);
      const needsApproval = Number((payload as { needs_approval?: number }).needs_approval ?? 0);
      if (skipped > 0) {
        const parts = [`${(payload.updated ?? []).length.toLocaleString('de-DE')} geändert`];
        if (needsApproval > 0) parts.push(`${needsApproval.toLocaleString('de-DE')} brauchen erst eine Freigabe`);
        if (skipped - needsApproval > 0) parts.push(`${(skipped - needsApproval).toLocaleString('de-DE')} in Extraktion übersprungen`);
        setBulkError(`${parts.join(', ')}.`);
      }
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Status konnte nicht geändert werden.');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkApprove = async () => {
    if (tab !== 'incoming' || selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy('approve');
    setBulkError(null);
    try {
      const ids = Array.from(selectedIds);
      const response = await fetch('/api/invoices/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        approved?: string[];
        first_approval?: string[];
        failed?: Array<{ id: string; error: string }>;
      };
      if (!response.ok) throw new Error(payload.error || `Freigabe fehlgeschlagen (${response.status}).`);
      const approved = payload.approved?.length ?? 0;
      const first = payload.first_approval?.length ?? 0;
      const failed = payload.failed ?? [];
      const parts: string[] = [];
      if (approved > 0) parts.push(`${approved.toLocaleString('de-DE')} freigegeben`);
      if (first > 0) parts.push(`${first.toLocaleString('de-DE')} warten auf die zweite Freigabe`);
      if (failed.length > 0) {
        const reasons = Array.from(new Set(failed.map((f) => f.error))).slice(0, 2).join(' ');
        parts.push(`${failed.length.toLocaleString('de-DE')} nicht freigegeben: ${reasons}`);
      }
      if (failed.length > 0 || first > 0) setBulkError(`${parts.join(', ')}.`);
      await loadInvoices({ silent: true });
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Freigabe fehlgeschlagen.');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy('download');
    setBulkError(null);
    try {
      const ids = Array.from(selectedIds);
      const stamp = new Date().toISOString().slice(0, 10);
      const folderName =
        tab === 'quotes' ? `angebote_${stamp}` : tab === 'incoming' ? `eingangsrechnungen_${stamp}` : `rechnungen_${stamp}`;
      const response = await fetch('/api/invoices/bulk-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, folder_name: folderName }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload?.error || `Download fehlgeschlagen (${response.status}).`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folderName}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Download fehlgeschlagen.');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkDeleteOffers = async () => {
    if (!bulkDeleteEnabled || selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy('delete');
    setBulkError(null);
    try {
      const ids = Array.from(selectedIds);
      const response = await fetch('/api/invoices/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const payload = await response.json().catch(() => ({} as { error?: string; deleted?: number; ids?: string[] }));
      if (!response.ok) throw new Error((payload as { error?: string })?.error || 'Angebote konnten nicht gelöscht werden.');
      const deletedIds = new Set<string>(
        Array.isArray((payload as { ids?: string[] }).ids) ? (payload as { ids: string[] }).ids : ids,
      );
      setRows((prev) => prev.filter((row) => !deletedIds.has(row.id)));
      setKpiRows((prev) => prev.filter((row) => !deletedIds.has(row.id)));
      setTotal((prev) => Math.max(0, prev - deletedIds.size));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Angebote konnten nicht gelöscht werden.');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleSendDunningStage = React.useCallback(async (row: InvoiceDocument, stage: DunningStage) => {
    if (!isDunningStageEnabled(stage, row)) return;
    setDunningBusy({ rowId: row.id, stage });
    setDunningNotice(null);
    try {
      const dispatch = await fetchDispatchAccount('dunning');
      if (!dispatch.account_id && !dispatch.can_choose) {
        setDunningNotice({ type: 'need_mail' });
        return;
      }

      let recipientEmail = '';
      let recipientCc = '';
      let recipientName = (row.party_name ?? '').trim();

      if (row.lead_id && canViewProjects) {
        try {
          const leadRes = await fetch(`/api/leads/${encodeURIComponent(row.lead_id)}`, { credentials: 'include' });
          if (leadRes.ok) {
            const lead = (await leadRes.json()) as Partial<Lead>;
            // Rechnungskontakt bevorzugen
            const billingEmail = lead.organization?.billing_contact?.email?.trim() ?? '';
            recipientEmail = billingEmail || leadOutboundEmail(lead);
            // CC aus Organisation
            const ccList = Array.isArray(lead.organization?.billing_email_cc)
              ? (lead.organization.billing_email_cc as string[]).filter(Boolean)
              : [];
            recipientCc = ccList.join(', ');
            const fromContact = lead.contact ? contactDisplayName(lead.contact) : '';
            const fromLeadName = (lead.name ?? '').trim();
            recipientName = (fromContact || fromLeadName || recipientName).trim();
          } else {
            console.warn('[dunning] /api/leads lookup failed', leadRes.status);
          }
        } catch (err) {
          console.warn('[dunning] /api/leads lookup error', err);
        }
      }

      if (!recipientEmail) recipientEmail = fallbackRecipientEmail(row, recipientName, leads, contacts);

      let deadlineDays = 7;
      let emailBodyTemplate: string | null = null;
      try {
        const [companyRes, textsRes] = await Promise.all([
          fetch('/api/settings/company', { credentials: 'include' }),
          fetch(
            `/api/settings/document-texts?type=${encodeURIComponent(dunningStageToDocumentType(stage))}`,
            { credentials: 'include' },
          ),
        ]);
        if (companyRes.ok) {
          const company = (await companyRes.json()) as { dunning_deadline_days?: unknown };
          deadlineDays = normalizeDunningDeadlineDays(company.dunning_deadline_days);
        }
        if (textsRes.ok) {
          const texts = (await textsRes.json()) as { sendPdfEmailBodyTemplate?: string };
          emailBodyTemplate = resolveDunningEmailBodyTemplate(
            dunningStageToDocumentType(stage),
            texts.sendPdfEmailBodyTemplate,
          );
        }
      } catch {
        /* defaults */
      }

      const { subject, body } = buildDunningEmail(stage, {
        invoiceNumber: row.invoice_number,
        grossAmount: row.gross_amount,
        currency: row.currency || 'EUR',
        dueDate: row.due_date,
        recipientName: recipientName || null,
        reminderSentAt: row.reminder_sent_at,
        dunning1SentAt: row.dunning_1_sent_at,
        dunning2SentAt: row.dunning_2_sent_at,
        deadlineDays,
        bodyTemplate: emailBodyTemplate,
      });

      const attachments: File[] = [];
      try {
        const pdfRes = await fetch(`/api/invoices/${encodeURIComponent(row.id)}/file`, { credentials: 'include' });
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          const filename = (row.filename || `${row.invoice_number || 'rechnung'}.pdf`).trim();
          attachments.push(new File([blob], filename, { type: blob.type || 'application/pdf' }));
        }
      } catch {
        /* attachment optional */
      }

      if (dunningLeadDocumentId(row, stage)) {
        try {
          const dunningRes = await fetch(`/api/invoices/${encodeURIComponent(row.id)}/dunning-document`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stage, existingOnly: true }),
          });
          if (dunningRes.ok) {
            const blob = await dunningRes.blob();
            const filename = dunningPdfFilename(stage, row.invoice_number);
            attachments.push(new File([blob], filename, { type: blob.type || 'application/pdf' }));
          } else {
            const column = DUNNING_STAGE_LEAD_DOCUMENT_COLUMN[stage];
            patchInvoiceRowState(row.id, { [column]: null });
          }
        } catch {
          /* existing dunning pdf optional */
        }
      }

      setDunningCompose({
        rowId: row.id,
        stage,
        invoiceNumber: row.invoice_number,
        accountId: dispatch.account_id,
        leadId: row.lead_id ? String(row.lead_id) : null,
        initialTo: recipientEmail,
        initialCc: recipientCc,
        initialSubject: subject,
        initialBody: body,
        initialAttachments: attachments,
        signatureHtml: dispatch.signature_html ?? null,
        fromEmail: dispatch.email,
      });
      setDunningComposeKey((k) => k + 1);
    } catch (err) {
      setDunningNotice({ type: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDunningBusy(null);
    }
  }, [canViewProjects, contacts, leads, patchInvoiceRowState]);

  const handleDunningSent = React.useCallback(async () => {
    if (!dunningCompose) return;
    const { rowId, stage } = dunningCompose;
    try {
      const res = await fetch(`/api/invoices/${encodeURIComponent(rowId)}/mark-dunning-sent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stage }),
      });
      if (res.ok) {
        const updated = (await res.json()) as InvoiceDocument;
        patchInvoiceRowState(rowId, updated);
      }
    } catch {
      /* ignore – next reload will pick it up */
    } finally {
      setDunningCompose(null);
    }
  }, [dunningCompose]);

  const handleSendDocumentMail = React.useCallback(async (row: InvoiceDocument, rowTab: InvoiceTab) => {
    const incoming = rowTab === 'incoming';
    // documentTypeLabelDe kennt nur die Ausgangs-Rechnungsarten.
    const label =
      rowTab === 'quotes'
        ? 'Angebot'
        : incoming
          ? row.document_type === 'incoming_invoice_correction'
            ? 'Lieferantengutschrift'
            : 'Eingangsrechnung'
          : documentTypeLabelDe(row.document_type);
    try {
      const dispatch = await fetchDispatchAccount(rowTab === 'quotes' ? 'offer' : incoming ? 'general' : 'invoice');
      if (!dispatch.account_id && !dispatch.can_choose) {
        setDunningNotice({ type: 'need_mail', purpose: 'document' });
        return;
      }

      let to = '';
      let cc = '';
      let recipientName = (row.party_name ?? '').trim();
      // Eingangsrechnungen gehen an beliebige Empfänger (z. B. Buchhaltung): Empfänger leer lassen.
      if (!incoming && row.lead_id && canViewProjects) {
        try {
          const leadRes = await fetch(`/api/leads/${encodeURIComponent(row.lead_id)}`, { credentials: 'include' });
          if (leadRes.ok) {
            const lead = (await leadRes.json()) as Lead;
            // Wie „Rechnung senden“ im Dokument-Editor.
            const billing = resolveDocumentBilling(lead, label, null);
            if (billing.email !== undefined) {
              to = billing.email;
              cc = (billing.cc ?? []).join(', ');
            } else {
              to = lead.organization?.billing_contact?.email?.trim() || leadOutboundEmail(lead);
              const ccList = Array.isArray(lead.organization?.billing_email_cc)
                ? (lead.organization.billing_email_cc as string[]).filter(Boolean)
                : [];
              cc = ccList.join(', ');
            }
            recipientName = contactDisplayName(lead.contact ?? { name: lead.name }).trim() || recipientName;
          }
        } catch {
          /* Fallbacks unten */
        }
      }
      if (!incoming && !to) to = fallbackRecipientEmail(row, recipientName, leads, contacts);

      const number = (row.invoice_number ?? '').trim();
      let subject = `${label} ${number}`.trim();
      let body = '';
      if (incoming) {
        const supplier = (row.party_name ?? '').trim();
        if (supplier) subject = `${subject} – ${supplier}`;
      } else {
        let template: string | null = null;
        try {
          const textsRes = await fetch(`/api/settings/document-texts?type=${encodeURIComponent(label)}`, {
            credentials: 'include',
          });
          if (textsRes.ok) {
            const texts = (await textsRes.json()) as { sendPdfEmailBodyTemplate?: string };
            template = texts.sendPdfEmailBodyTemplate ?? null;
          }
        } catch {
          /* Standardtext */
        }
        const greeting = recipientName ? `Sehr geehrter ${recipientName},` : 'Sehr geehrte Damen und Herren,';
        body = applySendPdfEmailBodyTemplate(template, greeting, label, {
          invoiceNumber: number,
          date: row.issue_date ? formatDate(row.issue_date) : '',
          amount: formatMoney(row.gross_amount, row.currency || 'EUR'),
          dueDate: row.due_date ? formatDate(row.due_date) : '',
        });
      }

      const pdfRes = await fetch(`/api/invoices/${encodeURIComponent(row.id)}/file`, { credentials: 'include' });
      if (!pdfRes.ok) throw new Error('Das PDF des Belegs konnte nicht geladen werden.');
      const blob = await pdfRes.blob();
      const filename = (row.filename || `${number || 'beleg'}.pdf`).trim();

      setDocumentMail({
        accountId: dispatch.account_id,
        leadId: row.lead_id ? String(row.lead_id) : null,
        initialTo: to,
        initialCc: cc,
        initialSubject: subject,
        initialBody: body,
        initialAttachments: [new File([blob], filename, { type: blob.type || 'application/pdf' })],
        signatureHtml: dispatch.signature_html ?? null,
        fromEmail: dispatch.email,
      });
      setDocumentMailKey((k) => k + 1);
    } catch (err) {
      setDunningNotice({
        type: 'error',
        purpose: 'document',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [canViewProjects, contacts, leads]);

  const handleAccountingForward = React.useCallback(async (row: InvoiceDocument) => {
    setAccountingForwardBusyId(row.id);
    setError(null);
    try {
      // Sage-Belege haben kein PDF und werden nicht versendet; die Übergabe wird nur vermerkt.
      const sageImport = row.source === 'sage_import';
      const res = sageImport
        ? await fetch(`/api/invoices/${encodeURIComponent(row.id)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ accounting_forwarded_at: true }),
        })
        : await fetch(`/api/invoices/${encodeURIComponent(row.id)}/forward-to-accounting`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });
      const payload = (await res.json().catch(() => ({}))) as InvoiceDocument & { error?: string; item?: InvoiceDocument };
      const updated = payload.item ?? (res.ok ? (payload as InvoiceDocument) : null);
      if (updated?.id) {
        patchInvoiceRowState(row.id, updated);
      }
      if (!res.ok) {
        throw new Error(payload.error || 'Weiterleitung an Buchhaltung fehlgeschlagen.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Weiterleitung an Buchhaltung fehlgeschlagen.');
    } finally {
      setAccountingForwardBusyId(null);
    }
  }, []);

  const renderEditableCell = (
    row: InvoiceDocument,
    field: EditableInvoiceField,
    display: React.ReactNode,
    className = '',
    editable = true,
  ) => {
    // Aus Sage übernommen: nur Zahlstatus und Zahldatum bleiben pflegbar.
    if (!editable || (row.source === 'sage_import' && !isSageImportPaymentField(field))) {
      return <td className={className}>{display}</td>;
    }
    const isEditing = editingCell?.rowId === row.id && editingCell.field === field;
    const type = field.includes('date') || field === 'paid_at' ? 'date' : 'text';
    if (isEditing) {
      if (field === 'lead_ref') {
        const q = leadSearch.trim().toLowerCase();
        const filteredLeads = (q ? leads.filter((lead) => leadDisplayLabel(lead).toLowerCase().includes(q)) : leads).slice(0, 50);
        return (
          <td className={`${className} relative`}>
            <InlineSelectPopover
              search={leadSearch}
              onSearch={setLeadSearch}
              items={filteredLeads}
              renderItem={leadDisplayLabel}
              pinnedItems={
                tab === 'incoming'
                  ? [
                      { id: '', label: 'Kein Auftrag' },
                      { id: INVOICE_OVERHEAD_ALLOCATION, label: 'Gemeinkosten' },
                    ]
                  : undefined
              }
              onSelectPinned={(id) => { void updateInvoiceRow(row, invoiceAuftragPatchFromSelect(id)); }}
              onSelect={(lead) => { void updateInvoiceRow(row, invoiceAuftragPatchFromSelect(String(lead.id))); }}
              onCreate={canCreateProjects ? () => {
                cancelEditing();
                setCreateLeadOpen(true);
              } : undefined}
              onClose={cancelEditing}
              createLabel="+ Neues Projekt erstellen"
              placeholder="Projekte suchen..."
            />
          </td>
        );
      }
      if (field === 'party_name') {
        const q = contactSearch.trim().toLowerCase();
        const filteredContacts = (q ? contacts.filter((contact) => contactOptionLabel(contact).toLowerCase().includes(q)) : contacts).slice(0, 50);
        return (
          <td className={`${className} relative`}>
            <InlineSelectPopover
              search={contactSearch}
              onSearch={setContactSearch}
              items={filteredContacts}
              renderItem={contactOptionLabel}
              onSelect={(contact) => { void updateInvoiceRow(row, { party_name: contactDisplayName(contact) || contact.company || contact.name || '' }); }}
              onCreate={canCreateContacts ? () => {
                cancelEditing();
                setCreateContactOpen(true);
              } : undefined}
              onClose={cancelEditing}
              createLabel="+ Neuen Kontakt erstellen"
              placeholder="Kontakte suchen..."
            />
          </td>
        );
      }
      if (field === 'organization_id') {
        const q = orgSearch.trim().toLowerCase();
        const orgs = supplierOrganizations(organizations, row.organization_id);
        const filteredOrgs = (q ? orgs.filter((org) => org.name.toLowerCase().includes(q)) : orgs).slice(0, 50);
        return (
          <td className={`${className} relative`}>
            <InlineSelectPopover
              search={orgSearch}
              onSearch={setOrgSearch}
              items={filteredOrgs}
              renderItem={(org) => org.name}
              onSelect={(org) => { void updateInvoiceRow(row, { organization_id: org.id, party_name: org.name }); }}
              onCreate={canCreateContacts ? () => {
                setCreateSupplierFor({ row, name: orgSearch.trim() || String(row.party_name ?? '').trim() });
                cancelEditing();
              } : undefined}
              onClose={cancelEditing}
              createLabel="+ Als Lieferant anlegen"
              placeholder="Lieferanten suchen..."
            />
          </td>
        );
      }
      if (field === 'issue_date' || field === 'due_date' || field === 'paid_at') {
        return (
          <td className={`${className} relative`}>
            <DateEditPopover
              value={editValue}
              onSave={(nextValue) => { void saveEditing(nextValue); }}
              onClose={cancelEditing}
            />
          </td>
        );
      }
      if (field === 'status') {
        return (
          <td className={`${className} relative`}>
            <StatusEditPopover
              value={getEditableValue(row, 'status', tab) as Exclude<InvoiceStatus, 'all'>}
              tab={tab}
              row={row}
              onSave={(nextValue) => { void saveEditing(nextValue); }}
              onClose={cancelEditing}
            />
          </td>
        );
      }
      return (
        <td className={className}>
          <input
            autoFocus
            type={type}
            inputMode={field === 'gross_amount' ? 'decimal' : undefined}
            pattern={field === 'gross_amount' ? '[0-9]*[,.]?[0-9]*' : undefined}
            value={editValue}
            disabled={savingCell}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => { void saveEditing(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void saveEditing();
              if (e.key === 'Escape') cancelEditing();
            }}
            className="w-full rounded-md border border-blue-200 bg-app-surface px-2 py-1 text-xs text-app-text outline-none"
          />
        </td>
      );
    }
    return (
      <td
        className={`${className} cursor-text`}
        onClick={() => startEditing(row, field)}
        title="Klicken zum Bearbeiten"
      >
        {display}
      </td>
    );
  };

  if (!instanceId) return null;
  if (!tabFromUrl) {
    return <Navigate to={invoicesOverviewPath(instanceId, 'outgoing')} replace />;
  }

  return (
    <div className="h-screen min-h-0 flex overflow-hidden bg-app-surface-muted text-app-text">
      <AppSidebar active="invoices" />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        <AppTopBar title="Rechnungen" />

        <main className="app-scrollbar flex-1 min-h-0 flex flex-col overflow-y-auto md:overflow-hidden overscroll-contain pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-0">
          <section className="shrink-0 grid grid-cols-2 md:grid-cols-4 gap-2 p-3 md:gap-3 md:p-5 bg-app-surface-muted border-b border-app-border">
            {kpiCards.map((card) => (
              <div key={card.label} className={`relative overflow-hidden rounded-xl border border-t-2 border-app-border ${card.accent} bg-app-surface p-3 md:p-4 shadow-sm`}>
                <div className="flex items-start justify-between gap-2 md:gap-3">
                  <div className="min-w-0">
                    <p className={`${FILTER_GROUP_LABEL_CLASS} truncate`}>{card.label}</p>
                    <p className="mt-0.5 md:mt-1 text-base md:text-xl font-bold text-app-text tabular-nums truncate">{card.value}</p>
                    <p className="mt-1 md:mt-2 text-xs text-app-text-subtle truncate">{card.meta}</p>
                  </div>
                  <div className={`hidden sm:inline-flex h-8 w-8 md:h-9 md:w-9 rounded-lg items-center justify-center shrink-0 ${card.color}`}>{card.icon}</div>
                </div>
              </div>
            ))}
          </section>

          <section className="shrink-0 bg-app-surface">
            <div className="flex items-stretch gap-1 border-b border-app-border px-3 md:px-5">
              {TAB_CONFIG.filter((item) => item.id !== 'quotes' || quotesTabAvailable).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    navigate(invoicesOverviewPath(instanceId, item.id));
                  }}
                  className={`inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2.5 md:py-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap -mb-px ${
                    tab === item.id
                      ? 'border-pipedrive-blue text-pipedrive-blue'
                      : 'border-transparent text-app-text-subtle hover:text-app-text'
                  }`}
                >
                  {item.icon}
                  <span className="md:hidden">{item.id === 'outgoing' ? 'Ausgang' : item.id === 'incoming' ? 'Eingang' : item.label}</span>
                  <span className="hidden md:inline">{item.label}</span>
                  {item.id === 'incoming' && reviewCount > 0 && can('web.rechnungen.approve') ? (
                    <span
                      className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-amber-800 tabular-nums dark:bg-amber-950 dark:text-amber-300"
                      title={`${reviewCount} ${reviewCount === 1 ? 'Rechnung' : 'Rechnungen'} in Prüfung`}
                    >
                      {reviewCount.toLocaleString('de-DE')}
                    </span>
                  ) : null}
                </button>
              ))}
              <div className="flex-1 min-w-2" />
              {datevStatus.enabled && can('web.rechnungen.datev') ? (
                <button
                  type="button"
                  onClick={() => setDatevOpen(true)}
                  className="my-auto h-9 shrink-0 inline-flex items-center gap-2 rounded-md bg-app-surface px-2.5 md:px-3 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                  title="DATEV-Export"
                >
                  <FileOutput className="w-4 h-4" />
                  <span className="hidden sm:inline">DATEV</span>
                </button>
              ) : null}
              <DatevExportModal open={datevOpen} onClose={() => setDatevOpen(false)} />
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="my-auto h-9 shrink-0 inline-flex items-center gap-2 rounded-md bg-app-surface px-2.5 md:px-3 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                title={uploadActionLabel}
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">{uploadActionLabel}</span>
              </button>
            </div>
          </section>

          <section className="shrink-0 bg-app-surface border-b border-app-border px-3 md:px-5 py-2.5 md:py-3 flex flex-col gap-2.5 md:flex-row md:flex-wrap md:items-center md:gap-3">
            <div className="flex items-center gap-1 overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0 pb-0.5 md:pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {statusOptionsForTab(tab).map((option) => {
                const isOn = selectedStatuses.has(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setPage(0);
                      setSelectedStatuses((prev) => {
                        const next = new Set(prev);
                        if (next.has(option.id)) next.delete(option.id);
                        else next.add(option.id);
                        return next;
                      });
                    }}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 h-9 text-xs font-normal ${statusFilterPillClass(option.id, isOn)}`}
                  >
                    <StatusIcon status={option.id} />
                    {statusDisplayLabel(option.id, tab)}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 md:ml-auto md:flex-wrap md:justify-end md:gap-3">
              {showRetentionFilter ? (
                <button
                  type="button"
                  onClick={() => {
                    setPage(0);
                    setRetentionHeldFilter((prev) => !prev);
                  }}
                  className={`h-9 shrink-0 inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs font-normal transition-colors ${
                    retentionHeldFilter
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                  }`}
                  title="Nur Rechnungen mit offenem Sicherheitseinbehalt"
                  aria-pressed={retentionHeldFilter}
                >
                  <Lock className="h-3.5 w-3.5" />
                  <span>Offene Einbehalte</span>
                </button>
              ) : null}
              <div className="relative shrink-0">
                <button
                  ref={periodButtonRef}
                  type="button"
                  onClick={() => setPeriodOpen((open) => !open)}
                  className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs font-normal transition-colors ${
                    periodActive
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                  }`}
                  title="Nach Jahr / Monat filtern (Rechnungsdatum)"
                  aria-haspopup="dialog"
                  aria-expanded={periodOpen}
                >
                  <Calendar className="w-4 h-4" />
                  <span>{periodLabel}</span>
                  {periodActive ? (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Zeitraum-Filter zurücksetzen"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPage(0);
                        setFilterYear(null);
                        setFilterMonth(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          setPage(0);
                          setFilterYear(null);
                          setFilterMonth(null);
                        }
                      }}
                      className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-blue-700 hover:bg-blue-100"
                    >
                      <X className="w-3 h-3" />
                    </span>
                  ) : null}
                </button>
                {periodOpen && (
                  <PeriodFilterPopover
                    anchorRef={periodButtonRef}
                    year={filterYear}
                    month={filterMonth}
                    availableYears={availableYears}
                    onChange={(nextYear, nextMonth) => {
                      setPage(0);
                      setFilterYear(nextYear);
                      setFilterMonth(nextMonth);
                    }}
                    onClose={() => setPeriodOpen(false)}
                  />
                )}
              </div>
              <div className="h-9 flex-1 min-w-0 md:min-w-[280px] md:flex-none flex items-center gap-2 rounded-md border border-app-border bg-app-surface-muted px-3">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full min-w-0 bg-transparent border-0 outline-none text-xs text-app-text placeholder:text-slate-400"
                />
              </div>
            </div>
          </section>

          {bulkSelectEnabled && selectedIds.size > 0 && (
            <section className="hidden md:flex shrink-0 bg-blue-50 border-b border-blue-200 px-5 py-2 flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-blue-800">
                {selectedIds.size.toLocaleString('de-DE')} ausgewählt
              </span>
              <span className="h-4 w-px bg-blue-200" aria-hidden />
              <button
                type="button"
                onClick={() => { void handleBulkDownload(); }}
                disabled={bulkBusy !== null}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-blue-200 bg-app-surface px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bulkBusy === 'download' ? <LoadingSpinner size="sm" /> : <Download className="w-3.5 h-3.5" />}
                Als ZIP herunterladen
              </button>
              {tab === 'incoming' && can('web.rechnungen.approve') && selectedInReviewCount > 0 && (
                <button
                  type="button"
                  onClick={() => { void handleBulkApprove(); }}
                  disabled={bulkBusy !== null}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-blue-200 bg-app-surface px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Nur Belege ohne Fehler und mit bestätigten Hinweisen werden freigegeben."
                >
                  {bulkBusy === 'approve' ? <LoadingSpinner size="sm" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {selectedInReviewCount === selectedIds.size ? 'Freigeben' : `Freigeben (${selectedInReviewCount.toLocaleString('de-DE')})`}
                </button>
              )}
              {tab === 'incoming' && (
                <button
                  type="button"
                  onClick={() => { void openSepaPreview(); }}
                  disabled={bulkBusy !== null}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-blue-200 bg-app-surface px-2.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {bulkBusy === 'sepa' ? <LoadingSpinner size="sm" /> : <Landmark className="w-3.5 h-3.5" />}
                  SEPA-Datei erstellen
                </button>
              )}
              {bulkStatusOptions.length > 0 ? (
              <div className="w-fit">
                <AppSelectDropdown
                  value=""
                  placeholder={bulkBusy === 'status' ? 'Wird geändert…' : 'Status ändern'}
                  disabled={bulkBusy !== null}
                  onChange={(value) => { void handleBulkStatus(value); }}
                  options={bulkStatusOptions.map((option) => ({
                    value: option.id,
                    label: statusDisplayLabel(option.id, tab),
                  }))}
                  triggerIcon={bulkBusy === 'status' ? <LoadingSpinner size="sm" /> : <CircleDot className="h-3.5 w-3.5" />}
                  triggerClassName="!min-h-0 h-9 !w-auto !rounded-md !border-blue-200 !bg-app-surface !px-2.5 !py-0 !text-xs !font-medium !text-blue-700 hover:!border-blue-200 hover:!bg-blue-50"
                  chevronClassName="!h-3.5 !w-3.5 !text-blue-700"
                />
              </div>
              ) : null}
              {bulkDeleteEnabled && (
                <button
                  type="button"
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={bulkBusy !== null}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-200 bg-app-surface px-2.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Löschen
                </button>
              )}
              <button
                type="button"
                onClick={clearSelection}
                disabled={bulkBusy !== null}
                className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-app-text-muted hover:bg-app-surface hover:text-app-text disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Auswahl aufheben
              </button>
            </section>
          )}

          {bulkSelectEnabled && bulkError && (
            <section className="hidden md:flex shrink-0 mx-5 mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{bulkError}</span>
              <button
                type="button"
                onClick={() => setBulkError(null)}
                className="ml-auto text-xs text-rose-700 hover:text-rose-900"
              >
                Schließen
              </button>
            </section>
          )}

          <section className="grow-0 md:flex-1 md:min-h-0 flex flex-col md:grid md:grid-rows-[minmax(0,1fr)_auto] bg-app-surface md:overflow-hidden">
            <div className="md:min-h-0 md:overflow-hidden flex flex-col md:flex-1">
              {error && (
                <div className="shrink-0 mx-3 md:mx-5 mt-3 md:mt-5 mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 flex gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              {loading && isMobile ? (
                <CardListSkeleton rows={8} label={loadingLabelForTab(tab)} />
              ) : !loading && rows.length === 0 ? (
                <div className="flex flex-1 min-h-[12rem] md:min-h-0 flex-col items-center justify-center gap-3 text-slate-400 py-10 md:py-0">
                  <FileText className="w-10 h-10" />
                  <span className="text-sm">Keine Einträge gefunden.</span>
                </div>
              ) : !loading && isMobile ? (
                <div>
                  {displayedRows.map((row) => (
                    <InvoiceMobileCard
                      key={row.id}
                      row={row}
                      tab={tab}
                      reviewStaleDays={reviewStaleDays}
                      customerLabel={invoiceCustomerColumnLabel(row, tab === 'incoming', partyDisplayContext)}
                      customerHref={invoicePartyHref(invoiceCustomerPartyTarget(row, tab === 'incoming', partyDisplayContext))}
                      supplierLabel={invoiceSupplierColumnLabel(row, tab === 'incoming', partyDisplayContext)}
                      supplierHref={invoicePartyHref(invoiceSupplierPartyTarget(row, tab === 'incoming', partyDisplayContext))}
                      onPreview={() => setPreviewRow(row)}
                      onEditStatus={() => {
                        if (isInvoiceStatusLockedDuringExtraction(row.status)) return;
                        setMobileStatusRow(row);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div
                  ref={setTableScrollNode}
                  className="app-scrollbar flex-1 min-h-0 overflow-x-auto overflow-y-auto overscroll-none"
                >
                <table className="w-full min-w-[1220px] text-xs">
                  <thead>
                    <tr>
                      {bulkSelectEnabled && (
                        <th
                          scope="col"
                          className="sticky top-0 z-10 w-8 border-b border-app-border bg-app-surface-muted pl-5 pr-1 py-2.5 text-left"
                        >
                          <input
                            ref={headerCheckboxRef}
                            type="checkbox"
                            className="h-3 w-3 rounded-sm border border-app-border-strong align-middle accent-blue-500 cursor-pointer disabled:opacity-40"
                            checked={allVisibleSelected}
                            disabled={displayedRows.length === 0}
                            onChange={toggleSelectAllVisible}
                            aria-label="Alle sichtbaren Einträge auswählen"
                          />
                        </th>
                      )}
                      {tableColumns.map((column, index) => {
                        const isFirst = index === 0;
                        const isLast = index === tableColumns.length - 1;
                        const align = column.align ?? (isLast ? 'right' : 'left');
                        const padding = isFirst && !bulkSelectEnabled ? 'pl-5 pr-4' : 'px-4';

                        if (!column.sortKey) {
                          const alignClass = align === 'right' ? 'text-right' : 'text-left';
                          return (
                            <th
                              key={column.id}
                              scope="col"
                              className={`sticky top-0 z-10 border-b border-app-border bg-app-surface-muted ${padding} py-2.5 text-xs font-medium text-app-text-subtle ${alignClass}`}
                            >
                              {column.label}
                            </th>
                          );
                        }

                        return (
                          <InvoiceListSortableHeader
                            key={column.id}
                            label={column.label}
                            column={column.sortKey}
                            activeColumn={sort}
                            dir={dir}
                            onSort={toggleSort}
                            className={padding}
                            align={align}
                          />
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-app-border">
                    {loading ? (
                      <TableSkeletonRows
                        columns={tableColumns.length + (bulkSelectEnabled ? 1 : 0)}
                        rows={10}
                        label={loadingLabelForTab(tab)}
                      />
                    ) : displayedRows.map((row) => {
                      const isSelected = bulkSelectEnabled && selectedIds.has(row.id);
                      const isIncoming = tab === 'incoming';
                      const customerLabel = invoiceCustomerColumnLabel(row, isIncoming, partyDisplayContext);
                      const customerHref = invoicePartyHref(invoiceCustomerPartyTarget(row, isIncoming, partyDisplayContext));
                      const supplierLabel = isIncoming
                        ? invoiceSupplierColumnLabel(row, true, partyDisplayContext)
                        : '';
                      const supplierHref = isIncoming
                        ? invoicePartyHref(invoiceSupplierPartyTarget(row, true, partyDisplayContext))
                        : null;
                      const customerDisplay = customerLabel && customerHref ? (
                        <InvoiceEntityLink to={customerHref} title="Kunde öffnen" className="truncate">
                          {customerLabel}
                        </InvoiceEntityLink>
                      ) : displayOrDash(customerLabel);
                      const supplierDisplay = isIncoming
                        ? isUnassignedIncomingSupplier(row) ? (
                            <UnassignedSupplierLabel name={supplierLabel} />
                          ) : supplierLabel && supplierHref ? (
                            <InvoiceEntityLink to={supplierHref} title="Lieferant öffnen" className="truncate">
                              {supplierLabel}
                            </InvoiceEntityLink>
                          ) : displayOrDash(supplierLabel)
                        : null;
                      return (
                      <tr key={row.id} className={`group/row ${isSelected ? 'bg-blue-500/10' : 'hover:bg-blue-500/5'}`}>
                        {bulkSelectEnabled && (
                          <td className="pl-5 pr-1 py-2 w-8">
                            <input
                              type="checkbox"
                              className="h-3 w-3 rounded-sm border border-app-border-strong align-middle accent-blue-500 cursor-pointer opacity-70 hover:opacity-100"
                              checked={isSelected}
                              onChange={() => toggleSelectRow(row.id)}
                              aria-label={`Eintrag ${getDisplayId(row)} auswählen`}
                              onClick={(event) => event.stopPropagation()}
                            />
                          </td>
                        )}
                        <td className={`${bulkSelectEnabled ? 'px-4' : 'pl-5 pr-4'} py-2 font-medium whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => setPreviewRow(row)}
                            className="group inline-flex items-center gap-1.5 text-app-text"
                          >
                            {/* Nur die Nummer wirkt wie ein Link; die Chips daneben nicht. */}
                            <span className="group-hover:text-app-text-muted group-hover:underline">{getDisplayId(row)}</span>
                            {isIncoming && isIncomingCreditDocumentType(row.document_type) ? <IncomingCreditChip /> : null}
                            {isIncoming && row.duplicate_of ? <IncomingInvoiceDuplicateChip /> : null}
                            {isIncoming ? <IncomingInvoiceReviewChip row={row} /> : null}
                            {isIncoming ? <IncomingReviewAttentionChip row={row} staleDays={reviewStaleDays} /> : null}
                          </button>
                        </td>
                        {renderEditableCell(
                          row,
                          'lead_ref',
                          row.lead_id ? (
                            <InvoiceEntityLink to={leadDetailPath({ id: row.lead_id })} title="Auftrag öffnen">
                              {row.lead_display_id || row.lead_id.slice(0, 8)}
                            </InvoiceEntityLink>
                          ) : invoiceAuftragDisplayLabel(row) || '—',
                          'px-4 py-2 text-app-text-subtle whitespace-nowrap'
                        )}
                        {renderEditableCell(
                          row,
                          'party_name',
                          customerDisplay,
                          'px-4 py-2 text-app-text max-w-[200px] truncate',
                          !isIncoming,
                        )}
                        {showSupplierColumn
                          ? renderEditableCell(
                              row,
                              'organization_id',
                              supplierDisplay,
                              'px-4 py-2 text-app-text max-w-[200px] truncate',
                              isIncoming,
                            )
                          : null}
                        {renderEditableCell(row, 'issue_date', formatDate(row.issue_date), 'px-4 py-2 text-app-text-muted whitespace-nowrap')}
                        {renderEditableCell(
                          row,
                          'due_date',
                          <span className={dueDateTextClass(row)}>{formatDate(row.due_date)}</span>,
                          'px-4 py-2 text-app-text-muted whitespace-nowrap'
                        )}
                        {renderEditableCell(row, 'paid_at', formatDate(row.paid_at), 'px-4 py-2 text-app-text-subtle whitespace-nowrap')}
                        {showAccountingForwardColumn ? (
                          <td className="px-4 py-2 whitespace-nowrap text-center">
                            {isAccountingForwardableType(row.document_type)
                              && (accountingForwardEmailConfigured || row.source === 'sage_import') ? (
                              <AccountingForwardCell
                                row={row}
                                busy={accountingForwardBusyId === row.id}
                                onForward={() => { void handleAccountingForward(row); }}
                              />
                            ) : (
                              <span className="text-slate-300" aria-hidden>
                                —
                              </span>
                            )}
                          </td>
                        ) : null}
                        {renderEditableCell(row, 'gross_amount', <InvoiceAmountDisplay row={row} />, `px-4 py-2 text-right font-semibold whitespace-nowrap ${row.document_type === 'invoice_correction' ? 'text-rose-700' : isIncomingCreditDocumentType(row.document_type) ? 'text-emerald-700' : 'text-app-text'}`)}
                        {showRetentionColumn ? (
                          <td className="px-4 py-2 text-right whitespace-nowrap text-sm tabular-nums text-app-text-muted">
                            {toNumber(row.retention_gross) > 0 ? (
                              <span
                                className={`inline-flex items-center justify-end gap-1 ${
                                  row.retention_status === 'held' ? 'text-amber-800' : 'text-app-text-subtle'
                                }`}
                                title={
                                  row.retention_status === 'held'
                                    ? 'Sicherheitseinbehalt offen'
                                    : row.retention_status === 'released_on_final'
                                      ? 'Auf Schlussrechnung freigegeben'
                                      : 'Sicherheitseinbehalt'
                                }
                              >
                                {row.retention_status === 'held' ? <Lock className="h-3 w-3 shrink-0" /> : null}
                                {formatMoney(row.retention_gross, row.currency || 'EUR')}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        ) : null}
                        {renderEditableCell(row, 'status', (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${statusTextClass(row.status)}`}>
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDotClass(row.status)}`} aria-hidden />
                            {statusDisplayLabel(row.status, tab, row)}
                          </span>
                        ), 'px-4 py-2 whitespace-nowrap')}
                        {showDunningColumn ? (
                          <td className="px-4 py-2 whitespace-nowrap">
                            {row.document_type === 'invoice_correction' || row.document_type === 'credit_note' ? (
                              <span className="text-slate-300" aria-hidden>
                                —
                              </span>
                            ) : (
                              <DunningStageCell
                                row={row}
                                busyStage={dunningBusy?.rowId === row.id ? dunningBusy.stage : null}
                                onSendStage={(stage) => { void handleSendDunningStage(row, stage); }}
                              />
                            )}
                          </td>
                        ) : null}
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            {(() => {
                              const fileUrl = `/api/invoices/${encodeURIComponent(row.id)}/file`;
                              // Sage-Importe ohne verknüpftes PDF haben keine Datei.
                              const hasFile = !(row.source === 'sage_import' && !row.lead_document_id);
                              const isPdf =
                                String(row.mime_type ?? '').toLowerCase().includes('pdf') || /\.pdf$/i.test(row.filename ?? '');
                              const canReclassify = Boolean(invoiceReclassifyTarget(row)) && !invoiceReclassifyBlockReason(row);
                              const stornoAndCredit =
                                tab === 'outgoing' &&
                                row.source !== 'sage_import' &&
                                ['invoice', 'partial_invoice', 'final_invoice'].includes(row.document_type);
                              const reclassify =
                                canReclassify && (tab === 'incoming' || stornoAndCredit)
                                  ? {
                                      label: invoiceReclassifyActionLabel(tab === 'incoming' ? 'outgoing' : 'incoming'),
                                      onSelect: () => setReclassifyTarget(row),
                                    }
                                  : null;
                              return (
                                <>
                                  {/* Herunterladen und Drucken stehen im Menü. */}
                                  <InvoiceOutgoingRowMenu
                                    file={hasFile ? { url: fileUrl, filename: row.filename, isPdf } : null}
                                    onSendMail={hasFile ? () => { void handleSendDocumentMail(row, tab); } : null}
                                    showStornoAndCredit={stornoAndCredit}
                                    reclassify={reclassify}
                                    cancelled={row.status === 'cancelled'}
                                    hasLead={Boolean(row.lead_id)}
                                    hasLedgerSnapshot={Boolean(row.has_ledger_snapshot)}
                                    stornoHref={
                                      row.lead_id
                                        ? row.offering_id
                                          ? `${projectDocumentEditorPathFromLabel(
                                              { id: row.lead_id },
                                              'Stornorechnung',
                                              row.offering_id,
                                            )}&cancelInvoice=${encodeURIComponent(row.id)}`
                                          : `${leadDocumentPath(
                                              { id: row.lead_id },
                                              'Stornorechnung',
                                            )}?cancelInvoice=${encodeURIComponent(row.id)}`
                                        : null
                                    }
                                    creditHref={
                                      row.lead_id
                                        ? row.offering_id
                                          ? `${projectDocumentEditorPathFromLabel(
                                              { id: row.lead_id },
                                              'Gutschrift',
                                              row.offering_id,
                                            )}&creditInvoice=${encodeURIComponent(row.id)}`
                                          : `${leadDocumentPath(
                                              { id: row.lead_id },
                                              'Gutschrift',
                                            )}?creditInvoice=${encodeURIComponent(row.id)}`
                                        : null
                                    }
                                  />
                                </>
                              );
                            })()}
                            {isDeletableFromOverview(row) && (
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(row)}
                                className="h-7 w-7 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-700"
                                title="Angebot löschen"
                                aria-label="Angebot löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>
            <div className="shrink-0 border-t border-app-border flex flex-wrap items-center justify-between gap-2 px-3 md:px-5 py-3 mb-2 md:mb-0 text-xs text-app-text-subtle bg-app-surface">
              <span className="tabular-nums">
                {total === 0
                  ? '0 Einträge'
                  : `${rangeFrom.toLocaleString('de-DE')}–${rangeTo.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')} Einträgen`}
                {pageCount > 1 ? ` · Seite ${page + 1} / ${pageCount}` : ''}
              </span>
              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={page <= 0 || loading}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" aria-hidden />
                    Zurück
                  </button>
                  <button
                    type="button"
                    disabled={page >= pageCount - 1 || loading}
                    onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
                    className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Weiter
                    <ChevronRight className="w-3.5 h-3.5" aria-hidden />
                  </button>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      {uploadOpen && (
        <UploadInvoiceModal
          onClose={() => setUploadOpen(false)}
          onSaved={reloadInvoiceOverview}
          tab={tab}
          leads={leads}
          contacts={contacts}
          organizations={organizations}
          onCreateLead={canCreateProjects ? () => setCreateLeadOpen(true) : undefined}
          onCreateContact={canCreateContacts ? () => setCreateContactOpen(true) : undefined}
        />
      )}
      {previewRow && (
        <InvoiceDocumentPreviewModal
          row={rows.find((item) => item.id === previewRow.id) ?? previewRow}
          tab={invoiceTabForDocument(previewRow)}
          leads={leads}
          contacts={contacts}
          organizations={organizations}
          partyDisplayContext={partyDisplayContext}
          saving={savingCell}
          onPatch={(patch) => updateInvoiceRow(rows.find((item) => item.id === previewRow.id) ?? previewRow, patch)}
          onClose={() => setPreviewRow(null)}
          onOpenDuplicate={(id) => { void openInvoicePreview(id); }}
          onOrganizationCreated={addOrganizationOption}
          incomingCreditSources={rows}
          constructionDeductionsEnabled={constructionDeductionsEnabled}
          onReviewed={(next) => patchInvoiceRowState(next.id, next)}
        />
      )}
      {sepaOpen && instanceId ? (
        <IncomingSepaPreviewModal
          instanceId={instanceId}
          preview={sepaPreview}
          loading={sepaPreviewLoading}
          exporting={bulkBusy === 'sepa'}
          error={sepaError}
          executionDate={sepaExecutionDate}
          onExecutionDateChange={changeSepaExecutionDate}
          onClose={() => { setSepaOpen(false); setSepaError(null); }}
          onExport={() => { void exportSepaFile(); }}
        />
      ) : null}
      <AppConfirmDialog
        open={sepaSkontoPrompt != null}
        onClose={() => {
          if (!sepaSkontoBusy) setSepaSkontoPrompt(null);
        }}
        title="Skonto als gezogen markieren?"
        description={
          <div className="space-y-2">
            <p>
              In der SEPA-Datei wurde bei {sepaSkontoPrompt?.length === 1 ? 'einer Rechnung' : `${sepaSkontoPrompt?.length ?? 0} Rechnungen`}{' '}
              Skonto abgezogen. Als „gezogen“ markiert, stimmt der Zahlbetrag in der Rechnung mit der Überweisung überein.
            </p>
            <ul className="space-y-1 text-sm">
              {(sepaSkontoPrompt ?? []).map((item) => (
                <li key={item.id} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate">{[item.party_name, item.invoice_number].filter(Boolean).join(' · ') || 'Rechnung'}</span>
                  <span className="shrink-0 tabular-nums">
                    −{item.skonto_applied.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        }
        confirmLabel="Als gezogen markieren"
        cancelLabel="Nicht markieren"
        isLoading={sepaSkontoBusy}
        overlayClassName="z-[300]"
        onConfirm={markSepaSkontoTaken}
      />
      {deleteTarget && (
        <DeleteOfferModal
          row={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleOfferDeleted}
        />
      )}
      {bulkDeleteOpen && (
        <div
          className="fixed inset-0 z-[280] flex items-center justify-center bg-slate-900/45 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget && bulkBusy !== 'delete') setBulkDeleteOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-xl border border-app-border bg-app-surface shadow-2xl overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-app-border bg-app-surface-muted">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg inline-flex items-center justify-center bg-rose-50 text-rose-700">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-app-text">
                    {selectedIds.size === 1
                      ? 'Angebot löschen?'
                      : `${selectedIds.size.toLocaleString('de-DE')} Angebote löschen?`}
                  </h2>
                  <p className="text-xs text-app-text-subtle mt-0.5">Ausgewählte Angebote aus der Übersicht entfernen</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkBusy === 'delete'}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-app-text-muted disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-xs leading-5 text-app-text-muted">
                Die ausgewählten Angebote landen für 28 Tage im Papierkorb und können von dort wiederhergestellt
                werden. Die ursprünglichen Dokumente in den Aufträgen bleiben erhalten.
              </p>
              {bulkError && (
                <div className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{bulkError}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-app-border bg-app-surface-muted">
              <button
                type="button"
                onClick={() => setBulkDeleteOpen(false)}
                disabled={bulkBusy === 'delete'}
                className="rounded-lg border border-app-border bg-app-surface px-4 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => { void handleBulkDeleteOffers(); }}
                disabled={bulkBusy === 'delete'}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {bulkBusy === 'delete' && <LoadingSpinner />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
      {mobileStatusRow && (
        <MobileStatusPickerSheet
          row={rows.find((item) => item.id === mobileStatusRow.id) ?? mobileStatusRow}
          tab={tab}
          saving={savingCell}
          onClose={() => setMobileStatusRow(null)}
          onSave={(nextStatus) => {
            const row = rows.find((item) => item.id === mobileStatusRow.id) ?? mobileStatusRow;
            const current = invoiceStatusSelectValue(row.status, tab);
            if (nextStatus === current) {
              setMobileStatusRow(null);
              return;
            }
            void (async () => {
              const ok = await updateInvoiceRow(row, { status: nextStatus });
              if (ok) setMobileStatusRow(null);
            })();
          }}
        />
      )}
      {createLeadOpen && (
        <CreateLeadModal
          onClose={() => setCreateLeadOpen(false)}
          onCreated={(lead) => {
            setCreateLeadOpen(false);
            setLeads((prev) => [lead, ...prev]);
          }}
        />
      )}
      {createSupplierFor && (
        <CreateOrganizationModal
          initialName={createSupplierFor.name}
          initialType="Lieferant"
          onClose={() => setCreateSupplierFor(null)}
          onCreated={(org) => {
            const target = createSupplierFor.row;
            addOrganizationOption(org);
            setCreateSupplierFor(null);
            void updateInvoiceRow(target, { organization_id: String(org.id), party_name: org.name });
          }}
        />
      )}
      {createContactOpen && (
        <CreateContactModal
          onClose={() => setCreateContactOpen(false)}
          onCreated={(contact) => {
            setContacts((prev) => [contact, ...prev]);
          }}
        />
      )}
      {dunningCompose && (
        <ComposeEmailModal
          key={`dunning-mail-${dunningComposeKey}`}
          open
          accountId={dunningCompose.accountId}
          resetKey={dunningComposeKey}
          initialTo={dunningCompose.initialTo}
          initialCc={dunningCompose.initialCc}
          initialSubject={dunningCompose.initialSubject}
          initialBody={dunningCompose.initialBody}
          initialFromEmail={dunningCompose.fromEmail}
          initialAttachments={dunningCompose.initialAttachments}
          signatureHtml={dunningCompose.signatureHtml ?? null}
          overlayClassName="z-[270]"
          leadId={dunningCompose.leadId}
          dunningDocument={{
            invoiceId: dunningCompose.rowId,
            stage: dunningCompose.stage,
            invoiceNumber: dunningCompose.invoiceNumber,
          }}
          onClose={() => setDunningCompose(null)}
          onSent={handleDunningSent}
          onDunningDocumentCreated={(leadDocumentId) => {
            const column = DUNNING_STAGE_LEAD_DOCUMENT_COLUMN[dunningCompose.stage];
            patchInvoiceRowState(dunningCompose.rowId, { [column]: leadDocumentId });
          }}
        />
      )}
      {documentMail && (
        <ComposeEmailModal
          key={`document-mail-${documentMailKey}`}
          open
          accountId={documentMail.accountId}
          resetKey={documentMailKey}
          initialTo={documentMail.initialTo}
          initialCc={documentMail.initialCc}
          initialSubject={documentMail.initialSubject}
          initialBody={documentMail.initialBody}
          initialFromEmail={documentMail.fromEmail}
          initialAttachments={documentMail.initialAttachments}
          signatureHtml={documentMail.signatureHtml}
          overlayClassName="z-[270]"
          leadId={documentMail.leadId}
          onClose={() => setDocumentMail(null)}
          onSent={() => setDocumentMail(null)}
        />
      )}
      {reclassifyTarget ? (
        <AppConfirmDialog
          open
          onClose={() => {
            if (!reclassifyBusy) setReclassifyTarget(null);
          }}
          title={invoiceReclassifyActionLabel(invoiceReclassifyTarget(reclassifyTarget) ?? 'incoming')}
          description={
            <>
              „{reclassifyTarget.invoice_number || reclassifyTarget.filename}“ wird als{' '}
              {invoiceReclassifyTarget(reclassifyTarget) === 'incoming' ? 'Eingangsrechnung' : 'Ausgangsrechnung'} neu
              angelegt und erneut ausgelesen. Der bisherige Eintrag bleibt GoBD-konform als „Umgewandelt“ mit Verweis
              erhalten, die Umwandlung wird protokolliert.
            </>
          }
          confirmLabel="Umwandeln"
          isLoading={reclassifyBusy}
          overlayClassName="z-[300]"
          onConfirm={async () => {
            setReclassifyBusy(true);
            try {
              if (await reclassifyInvoiceRow(reclassifyTarget)) setReclassifyTarget(null);
            } finally {
              setReclassifyBusy(false);
            }
          }}
        />
      ) : null}
      <AppConfirmDialog
        open={rejectPrompt != null}
        onClose={() => {
          if (!savingCell) setRejectPrompt(null);
        }}
        title="Beleg ablehnen"
        description={
          <div className="space-y-2">
            <p>Warum wird der Beleg abgelehnt? Er bleibt archiviert, wird aber nicht gebucht.</p>
            <div className="space-y-1" role="radiogroup" aria-label="Ablehnungsgrund">
              {USER_SELECTABLE_REJECTION_REASONS.map((reason) => (
                <label key={reason} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-app-text hover:bg-app-hover">
                  <input
                    type="radio"
                    name="rejection-reason"
                    value={reason}
                    checked={rejectReasonDraft === reason}
                    onChange={() => setRejectReasonDraft(reason)}
                  />
                  {INVOICE_REJECTION_REASON_LABEL[reason]}
                </label>
              ))}
            </div>
          </div>
        }
        confirmLabel="Ablehnen"
        destructive
        isLoading={savingCell}
        overlayClassName="z-[300]"
        onConfirm={async () => {
          if (!rejectPrompt) return;
          const ok = await updateInvoiceRow(rejectPrompt.row, { ...rejectPrompt.patch, rejection_reason: rejectReasonDraft });
          if (ok) setRejectPrompt(null);
        }}
      />
      <AppConfirmDialog
        open={completeProjectPrompt != null}
        onClose={() => {
          if (!completingProject) setCompleteProjectPrompt(null);
        }}
        title="Projekt abschließen?"
        description={
          completeProjectPrompt ? (
            <>
              Die Rechnung wurde als bezahlt markiert. Soll das Projekt „{completeProjectPrompt.projectLabel}“ auf
              Gewonnen gestellt werden?
            </>
          ) : null
        }
        confirmLabel="Ja, auf Gewonnen"
        cancelLabel="Nein"
        isLoading={completingProject}
        overlayClassName="z-[300]"
        onConfirm={() => void confirmCompleteProject()}
      />
      {dunningNotice && (
        <div
          className="fixed inset-0 z-[280] flex items-center justify-center bg-slate-900/45 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) setDunningNotice(null);
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-app-surface shadow-2xl border border-app-border overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="px-5 pt-5 pb-2 text-center">
              <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-amber-100 text-amber-700">
                <AlertCircle className="h-5 w-5" />
              </div>
              <h3 className="text-sm font-semibold text-app-text">
                {dunningNotice.type === 'need_mail'
                  ? 'Postfach erforderlich'
                  : dunningNotice.purpose === 'document'
                    ? 'E-Mail konnte nicht vorbereitet werden'
                    : 'Mahnung konnte nicht gestartet werden'}
              </h3>
            </div>
            <div className="px-5 pb-4 text-sm text-app-text-muted leading-relaxed text-center">
              {dunningNotice.type === 'need_mail' ? (
                <p>
                  Um {dunningNotice.purpose === 'document' ? 'Belege' : 'eine Mahnung'} per E-Mail zu versenden, verbinden Sie
                  bitte zuerst ein Postfach in den Einstellungen.
                </p>
              ) : (
                <p className="whitespace-pre-wrap">{dunningNotice.message}</p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-app-border flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDunningNotice(null)}
                className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
              >
                Schließen
              </button>
              {dunningNotice.type === 'need_mail' && (
                <Link
                  to={`/i/${encodeURIComponent(instanceId ?? 'default')}/settings/email-dispatch`}
                  onClick={() => setDunningNotice(null)}
                  className="inline-flex justify-center items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                >
                  E-Mail einrichten
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
